import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { ScrollableList } from "@/components/ScrollableList";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sparkles,
  X,
  ZoomIn,
  ZoomOut,
  EyeOff,
  Eye,
  ClipboardPaste,
  Highlighter,
  GripVertical,
  UserRoundPlus,
  UserRoundX,
  Share2,
} from "lucide-react";
import { useScheduleFeedback } from "@/hooks/useScheduleFeedback";
import { ScheduleFeedbackMarker } from "@/components/ScheduleFeedbackMarker";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { toast } from "sonner";
import { AiGenerationDialog } from "./AiGenerationDialog";
import { AiRegenerateDialog } from "./AiRegenerateDialog";
import ShareScheduleDialog from "./ShareScheduleDialog";
import ScheduleFeedbackPanel from "./ScheduleFeedbackPanel";
import { ArtistPhoto } from "./schedule/ArtistPhoto";
import { FilmedByList } from "./schedule/FilmedByList";
import { useArtistImagePrefetch } from "@/hooks/useArtistImagePrefetch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import {
  stagesForDay,
  resolveScheduleStages,
  type BriefingStageRecord,
} from "@/lib/briefingStages";
import { getStageLabel } from "@/lib/stageLabel";
import { artistGlowVisible } from "@/lib/lineupHighlight";
import { applyStageFilter, toggleStage } from "@/lib/stageFilterUi";
import {
  applyFunctionFilter,
  sortCrew,
  toggleFunction,
  CREW_SORT_LABELS,
  type CrewSortOrder,
} from "@/lib/crewFunctionFilter";
import { FunctionFilterBar } from "@/components/schedule/FunctionFilterBar";
import { addMinutesToTime, artistAtTime } from "@/lib/stageDrop";
import { isCustomForStage, isStageBoundCustom } from "@/lib/stageAssignments";
import { buildGenerateScheduleBody } from "@/lib/aiScheduleRequest";
import { StageFilterBar } from "@/components/schedule/StageFilterBar";
import { isCrewMemberVisible } from "@/lib/scheduleVisibility";
import { useStageReorder } from "@/hooks/useStageReorder";
import { useReorderStages } from "@/hooks/useReorderStages";
import { deriveMirrorAssignments, type AssistantLink } from "@/lib/deriveMirrorAssignments";
import { ScheduleHistoryButton } from "@/components/ScheduleHistoryButton";
import { useScheduleAutosave } from "@/hooks/useScheduleAutosave";
import { captureScheduleDraft } from "@/lib/scheduleDraft";
import { sortStackedAssignments } from "@/lib/stackOrder";
import {
  festivalDayIso,
  isAssistableRole,
  isAssistantRole,
  resolveScheduleStartHour,
  horizontalPositionForTime,
} from "@produce/shared";
import type { AiGenerationSettings } from "@/types/domain";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { AssignmentContextMenu, ITEM_FOCUS } from "./schedule/AssignmentContextMenu";
import { ScheduleRowResizeHandles } from "./schedule/ScheduleRowResizeHandles";
import { ResizablePanelSection } from "./schedule/ResizablePanelSection";
import { useScheduleRowHeights } from "@/hooks/useScheduleRowHeights";
import { useScheduleLabelWidth } from "@/hooks/useScheduleLabelWidth";
import { useCurrentTimeMarker } from "@/hooks/useCurrentTimeMarker";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { CREATOR_LAYERS } from "@/lib/scheduleLayers";
import { LABEL_SEAM_COVER, ROW_SEAM } from "@/lib/scheduleRowSeams";
import { CurrentTimeMarkerLine } from "./schedule/CurrentTimeMarkerLine";
import { CurrentTimeMarkerPill } from "./schedule/CurrentTimeMarkerPill";
import { TimelineHourLabels } from "./schedule/TimelineHourLabels";
import { useSchedulePanelHeight } from "@/hooks/useSchedulePanelHeight";
import {
  DEFAULT_CREW_LIST_HEIGHT,
  PANEL_CREW_LIST,
  PANEL_TIMELINE,
  defaultTimelineHeight,
} from "@/lib/schedulePanelHeights";
import {
  customRowKey,
  dutyBlockLayout,
  stackedBlockLayout,
  stageDutyDropTop,
  stageDutyStrip,
  stageDutyStripLanes,
  stageRowKey,
} from "@/lib/scheduleRowHeights";
import { assignRowLanes, maxLaneCount } from "@/lib/scheduleRowLanes";
import { getEffectiveAssignmentTimes } from "@/lib/assignmentResizePreview";
import { resolveResizeTime } from "@/lib/resizeAssignmentTime";
import { useAssignmentClipboard } from "@/hooks/useAssignmentClipboard";
import { useDragSessionGuard } from "@/hooks/useDragSessionGuard";
import {
  artistBinding,
  rebaseAssignment,
  rebindToArtist,
  rebindToStage,
  clampEndToSetEnd,
  normalizeTime,
  shiftTime,
} from "@/lib/assignmentClipboard";
import { matchAssignments, withClientId, assignmentStableId } from "@/lib/assignmentIdentity";
import { useRemovalAnimation } from "@/hooks/useRemovalAnimation";
import {
  initHistory,
  pushHistory,
  undo as undoHistory,
  redo as redoHistory,
  canUndo as historyCanUndo,
  canRedo as historyCanRedo,
  type History,
} from "@/lib/assignmentHistory";
import { matchScheduleShortcut } from "@/lib/scheduleShortcuts";
import {
  positionToSnappedTime,
  positionToSnappedTimeMulti,
  clockDurationMinutes,
  fitBlockIntoSet,
  HOUR_WIDTH_PX as HOUR_WIDTH,
} from "@/lib/timeSnap";
import { crewMemberHasConflict, timeRangesOverlap } from "@/lib/assignmentConflict";

// How long (ms) after a context-menu interaction to ignore the stray click that
// Radix's pointerup→unmount sequence retargets onto the row/block underneath.
const CONTEXT_CLICK_SUPPRESS_MS = 700;

// One crew member can't film two things at once — shown by every placement path
// that detects a double-booking, kept in one place so the wording stays in sync.
const CREW_CONFLICT_MESSAGE =
  "This crew member already has an assignment at this time. Please resolve the conflict first.";

// Inline style that pops a block out (shrink + fade) while it is animating away
// on delete. The transition is always present so the change to scale(0)/opacity
// animates; only transform + opacity transition, so resize/drag stay instant.
// Two shared frozen objects — spread into each block's style — so the common
// (idle) case doesn't allocate a fresh object per block per render.
const POP_OUT_TRANSITION = "transform 150ms ease-in, opacity 150ms ease-in";
const POP_OUT_IDLE: React.CSSProperties = { transition: POP_OUT_TRANSITION };
const POP_OUT_LEAVING: React.CSSProperties = {
  transition: POP_OUT_TRANSITION,
  transform: "scale(0)",
  opacity: 0,
  pointerEvents: "none",
};
const popOutStyle = (leaving: boolean): React.CSSProperties =>
  leaving ? POP_OUT_LEAVING : POP_OUT_IDLE;

// Default lane heights; per-row overrides come from useScheduleRowHeights.
const DEFAULT_STAGE_ROW_HEIGHT = 80;
const DEFAULT_CUSTOM_ROW_HEIGHT = 60;
// Default width of the sticky label column; adjustable via the drag handle
// on the labels' right edge (useScheduleLabelWidth).
const DEFAULT_LABEL_WIDTH = 120;
// Vertical breathing room around a block inside its lane: 4px (top-1) above
// and the same below.
const ROW_BLOCK_PADDING = 8;

interface CrewMember {
  id?: string;
  name: string;
  function: string;
  color?: string;
  event_date: string;
}

interface Artist {
  id?: string;
  artist_name: string;
  stage_id?: string | null;
  stage_name: string;
  set_start: string;
  set_end: string;
  event_date: string;
  // Producer-marked "important" artist (set in the lineup import tab).
  // Rendered with an outer glow / thicker border below so it stands
  // out while building the planning.
  highlighted?: boolean;
}

interface Assignment {
  id?: string;
  crew_member_id: string;
  event_date: string;
  artist_name?: string;
  stage_id?: string | null;
  stage_name: string;
  lineup_id?: string | null;
  filming_start: string;
  filming_end: string;
  assignment_type: "artist" | "custom";
  subject?: string;
  location?: string;
  notes?: string;
  mirrored_from_id?: string | null;
  // Per-slot opt-out: when true this source assignment is NOT mirrored to the
  // crew member's assistant.
  mirror_excluded?: boolean;
  // Assistants attached to just this assignment (per-slot, alongside any
  // whole-day assist link).
  assist_crew_member_ids?: string[];
  // Transient client-side flags for assistant mirror PREVIEW blocks. Never
  // persisted — derived at render from the assisted member's assignments.
  __mirror?: boolean;
  __mirrorSourceName?: string;
}

interface CrewScheduleProps {
  festivalDays: Array<{ date: string; startTime: string; endTime: string }>;
  stages: Array<{ name: string; label: string }>;
  crew: CrewMember[];
  timetable: Artist[];
  assignments: Assignment[];
  onAssignmentsUpdate: (assignments: Assignment[]) => void;
  onCustomSubjectsUpdate?: (
    subjects: Array<{ name: string; label: string; location?: string }>,
  ) => void;
  briefingId: string;
  onSave?: () => Promise<void>;
  defaultAssignmentLength?: number; // 15, 30, 45, or 60 minutes
}

export function CrewSchedule({
  festivalDays,
  stages,
  crew,
  timetable,
  assignments,
  onAssignmentsUpdate,
  onCustomSubjectsUpdate,
  briefingId,
  onSave,
  defaultAssignmentLength = 15,
}: CrewScheduleProps) {
  // Warm the artist photos so clicking an artist shows the photo straight
  // away. URLs for the whole production (cheap, and switching days is a click
  // too); the actual image bytes only for the day on screen, since a
  // multi-day lineup is tens of megabytes and the producer can only click
  // what's in front of them. Declared after selectedDate, further down.

  // Initialize with current date if it's a festival day, otherwise first day
  const getInitialDate = () => {
    if (festivalDays.length === 0) return "";
    const today = festivalDayIso();
    const isFestivalDay = festivalDays.some((day) => day.date === today);
    return isFestivalDay ? today : festivalDays[0].date;
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedCrewFilter, setSelectedCrewFilter] = useState<string>("all");
  const [activatedArtists, setActivatedArtists] = useState<Set<string>>(new Set());
  const [activatedCrew, setActivatedCrew] = useState<Set<string>>(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customSubject, setCustomSubject] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  const [customStageId, setCustomStageId] = useState<string | null>(null);
  const [customNotes, setCustomNotes] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedCrewForCustom, setSelectedCrewForCustom] = useState("");
  // Multi-select stage filter (empty = show all stages). Shared filter
  // logic with the read-only producer view via stageFilterUi.
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());
  // Function filter + sort order for the "Drag to assign" pick list only.
  const [crewListFunctions, setCrewListFunctions] = useState<Set<string>>(new Set());
  const [crewListSort, setCrewListSort] = useState<CrewSortOrder>("crew");
  const [filmingStart, setFilmingStart] = useState("");
  const [filmingEnd, setFilmingEnd] = useState("");
  const [artistNotes, setArtistNotes] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  // Per-row adjustable heights (persisted per briefing in localStorage).
  const rowHeights = useScheduleRowHeights(briefingId);
  // Autosave: periodic recoverable draft snapshots (not published to crew).
  // Surfaced in the History dialog as "Autosave", distinct from manual saves.
  const autosave = useScheduleAutosave({ briefingId, assignments, enabled: !!briefingId });

  // Capture the current (possibly unsaved) editor draft as a snapshot before a
  // restore overwrites it, so the reload never silently loses in-progress edits
  // — they stay recoverable. Reuses the shared capture (which owns the
  // skip-incomplete-draft rule) instead of re-implementing it here.
  const captureDraftBeforeRestore = useCallback(async () => {
    if (!briefingId) return;
    await captureScheduleDraft(
      (name, opts) => supabase.functions.invoke(name, opts),
      briefingId,
      assignments,
    );
  }, [briefingId, assignments]);
  // Adjustable label-column width (same persistence mechanism).
  const labelWidthCtl = useScheduleLabelWidth(briefingId, "creator", DEFAULT_LABEL_WIDTH);
  const { labelWidth } = labelWidthCtl;
  // Adjustable heights for the two panels this component owns. The schedule
  // window defaults to the browser height (as the old CSS calc did) until the
  // producer drags it; the crew list starts tall enough to show ~6 rows.
  const timelinePanel = useSchedulePanelHeight(briefingId, PANEL_TIMELINE, defaultTimelineHeight());
  const crewListPanel = useSchedulePanelHeight(
    briefingId,
    PANEL_CREW_LIST,
    DEFAULT_CREW_LIST_HEIGHT,
  );
  const [draggedCrew, setDraggedCrew] = useState<string | null>(null);
  const [draggedAssignment, setDraggedAssignment] = useState<Assignment | null>(null);
  const [isDraggingBlock, setIsDraggingBlock] = useState(false);
  const [resizingAssignment, setResizingAssignment] = useState<{
    assignment: Assignment;
    originalAssignment: Assignment;
    edge: "start" | "end";
  } | null>(null);
  const [justFinishedResizing, setJustFinishedResizing] = useState(false);
  const [selectedAssignmentForEdit, setSelectedAssignmentForEdit] = useState<Assignment | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  // Undo/redo stack. The pure History helpers own the cursor logic so undo,
  // redo and "new edit drops the redo branch" can't desync (see lib/assignmentHistory).
  const [history, setHistory] = useState<History<Assignment[]>>(() => initHistory([]));
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiRegenerateDialogOpen, setAiRegenerateDialogOpen] = useState(false);
  // Dynamically check if selected date has assignments (replaces hasGeneratedWithAi state)
  const hasAssignmentsForSelectedDate = assignments.some((a) => a.event_date === selectedDate);
  const [previousAiSettings, setPreviousAiSettings] = useState<AiGenerationSettings | null>(null);
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const [hiddenCrew, setHiddenCrew] = useState<Set<string>>(new Set());
  // Toggle for the green outer glow / ring around highlighted artists.
  // Producers can hide it to reduce visual noise while building the planning.
  const [showHighlights, setShowHighlights] = useState(true);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Copy / cut / paste of assignment blocks.
  // - `clipboard` holds the single copied/cut block (session-scoped, never the OS clipboard).
  // - `hoveredAssignmentRef` tracks the block under the cursor so Ctrl/Cmd+C/X act on it.
  // - `pasteAnchorRef` tracks where Ctrl/Cmd+V would land, updated on mousemove / right-click.
  //   A "crew" anchor (custom row) relocates the block to that crew + time; an "artist"
  //   anchor (artist row) re-binds it to that artist, keeping the source crew member.
  // - `suppressBlockClickRef` holds a timestamp until which row/block clicks are
  //   ignored, so the stray click that follows a context-menu selection (or a
  //   macOS Control-click) doesn't also open a dialog. See isStrayMenuClick.
  type PasteAnchor =
    | { kind: "crew"; crewMemberId: string; startTime: string }
    | { kind: "artist"; artist: Artist; startTime: string }
    | { kind: "stage"; stage: BriefingStageRecord; startTime: string };
  const clipboard = useAssignmentClipboard<Assignment>();
  // Drives the "floep weg" pop-out when an assignment is deleted (see below).
  const blockRemoval = useRemovalAnimation();
  // Pop-out style for a block, spread into its inline style at each render site.
  // Skips computing the block's identity entirely when nothing is animating out
  // (the near-constant case).
  const leavingStyle = (assignment: Assignment): React.CSSProperties =>
    popOutStyle(blockRemoval.hasLeaving && blockRemoval.isLeaving(assignmentStableId(assignment)));
  // Always holds the latest assignments so deferred commits (the delete pop-out)
  // filter from current state, not a snapshot captured 160ms earlier.
  const assignmentsRef = useRef(assignments);
  assignmentsRef.current = assignments;
  const hoveredAssignmentRef = useRef<Assignment | null>(null);
  const pasteAnchorRef = useRef<PasteAnchor | null>(null);
  const suppressBlockClickRef = useRef(0);
  // Cached Radix ScrollArea viewport so the per-mousemove paste-anchor math
  // doesn't re-run querySelector on every cursor move across the timeline.
  const scrollViewportRef = useRef<HTMLElement | null>(null);
  // The element that actually scrolls sits inside the Radix ScrollArea. Cached
  // because the drag math resolves it per pointer move; re-queried whenever the
  // panel remounted the viewport underneath us.
  const resolveScrollViewport = useCallback(() => {
    let viewport = scrollViewportRef.current;
    if (!viewport || !viewport.isConnected) {
      viewport = (timelineRef.current?.querySelector("[data-radix-scroll-area-viewport]") ??
        null) as HTMLElement | null;
      scrollViewportRef.current = viewport;
    }
    return viewport;
  }, []);

  // A left-click is "stray" when it immediately follows a context-menu open or
  // selection (Radix unmounts the menu on pointerup, then the native click
  // retargets onto the row/block underneath) or is a macOS Control-click.
  const isStrayMenuClick = (e: React.MouseEvent) =>
    e.ctrlKey || Date.now() < suppressBlockClickRef.current;
  const suppressNextClick = () => {
    suppressBlockClickRef.current = Date.now() + CONTEXT_CLICK_SUPPRESS_MS;
  };

  // Producer settings for current time marker
  const { settings: producerSettings } = useProducerSettings();

  const formatTime = useProducerTimeFormatter();

  // Canonical stages from the post-redesign briefing_stages table.
  // Used to drive the stage dropdown for custom (non-artist) assignments
  // so an assignment like "Publiek op Mainstage" carries a real
  // stage_id and rolls up correctly in analytics.
  const { data: briefingStages = [] } = useQuery({
    queryKey: ["briefing_stages", briefingId],
    enabled: !!briefingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefing_stages")
        .select("*")
        .eq("briefing_id", briefingId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BriefingStageRecord[];
    },
  });
  const stagesForSelectedDate = stagesForDay(briefingStages, selectedDate);

  // Client feedback mirrored ONTO the editable schedule (markers), so the
  // producer sees and resolves it right where they edit. Grouped per row label.
  const {
    openItems: feedbackOpenItems,
    resolve: resolveFeedback,
    remove: removeFeedback,
  } = useScheduleFeedback(briefingId);
  const feedbackByRow = useMemo(() => {
    const map = new Map<string, typeof feedbackOpenItems>();
    for (const f of feedbackOpenItems) {
      if (f.event_date !== selectedDate || !f.stage_name || !f.anchor_time) continue;
      const arr = map.get(f.stage_name) ?? [];
      arr.push(f);
      map.set(f.stage_name, arr);
    }
    return map;
  }, [feedbackOpenItems, selectedDate]);

  const queryClient = useQueryClient();

  // Persist a new stage order for the selected day (optimistic; shared hook).
  const reorderStagesMutation = useReorderStages(briefingId, selectedDate);

  // Drag-and-drop reordering of the stage rows. Operates on the FULL day order
  // (including empty stages) so display_order stays a clean 0..n-1 permutation.
  const stageReorder = useStageReorder(
    stagesForSelectedDate.map((s) => s.id),
    (orderedIds) => reorderStagesMutation.mutate(orderedIds),
  );

  // Dedicated-assistant links for this briefing's crew (member id → assisted
  // member id). Kept self-contained here so the assist UI doesn't need to be
  // threaded through the parent's crew state. The actual schedule mirroring
  // happens server-side in replace_crew_assignments on the next save.
  const { data: assistLinks = {} } = useQuery({
    queryKey: ["crew_assist_links", briefingId],
    enabled: !!briefingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("id, assists_crew_member_id")
        .eq("briefing_id", briefingId);
      if (error) throw error;
      const map: Record<string, string | null> = {};
      (data ?? []).forEach((r) => {
        map[r.id] = r.assists_crew_member_id;
      });
      return map;
    },
  });

  const setAssistMutation = useMutation({
    mutationFn: async ({ memberId, assistsId }: { memberId: string; assistsId: string | null }) => {
      const { error } = await supabase
        .from("crew_members")
        .update({ assists_crew_member_id: assistsId })
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crew_assist_links", briefingId] });
      toast.success("Assistant link saved. Click Save to mirror the schedule.");
    },
    onError: () => toast.error("Couldn't save the assistant link"),
  });

  const memberNameById = useMemo(
    () => new Map(crew.filter((c) => c.id).map((c) => [c.id as string, c.name])),
    [crew],
  );

  // Assistant links resolved to (assistant → assisted member) with the
  // assisted member's display name, used both for the legend summary and to
  // derive the live mirror PREVIEW blocks on the timeline.
  const assistantLinks: AssistantLink[] = useMemo(
    () =>
      Object.entries(assistLinks)
        .filter(([, sourceId]) => !!sourceId)
        .map(([assistantId, sourceId]) => ({
          assistantId,
          sourceMemberId: sourceId as string,
          sourceName: memberNameById.get(sourceId as string) ?? "",
        })),
    [assistLinks, memberNameById],
  );

  // Read-only preview blocks: clones of each assisted member's assignments,
  // re-attributed to their assistant. Never persisted, never sent on save —
  // purely so the producer sees the mirrored schedule the moment they link.
  const mirrorAssignments = useMemo(
    () => deriveMirrorAssignments(assignments, assistantLinks, memberNameById),
    [assignments, assistantLinks, memberNameById],
  );
  const assignmentsWithMirrors = useMemo(
    () => [...assignments, ...mirrorAssignments],
    [assignments, mirrorAssignments],
  );

  // Members who have at least one dedicated assistant — used to gate the
  // per-slot "remove assist" menu item.
  const assistedMemberIds = useMemo(
    () => new Set(Object.values(assistLinks).filter(Boolean) as string[]),
    [assistLinks],
  );
  const canExcludeAssist = (crewMemberId: string) => assistedMemberIds.has(crewMemberId);

  // Toggle whether a single source assignment is mirrored to its assistant.
  // The rendered source block is the same object reference held in `assignments`
  // (mirror previews are appended separately), so an identity match is safe.
  const handleToggleMirrorExcluded = (target: Assignment) => {
    saveToHistory(
      assignments.map((a) => (a === target ? { ...a, mirror_excluded: !a.mirror_excluded } : a)),
    );
  };

  // Check if selected date is today
  const isToday = selectedDate === festivalDayIso();

  // Update selectedDate when festivalDays changes
  useEffect(() => {
    if (festivalDays.length === 0) return;

    // If no date is selected, or the selected date is not in festivalDays, set default
    const isValidDate = festivalDays.some((day) => day.date === selectedDate);

    if (!selectedDate || !isValidDate) {
      const today = festivalDayIso();
      const isFestivalDay = festivalDays.some((day) => day.date === today);
      setSelectedDate(isFestivalDay ? today : festivalDays[0].date);
    }
  }, [festivalDays, selectedDate]);

  // Update activated artists when timetable changes
  useEffect(() => {
    const newActivated = new Set<string>();
    timetable.forEach((artist) => {
      const key = `${artist.artist_name}-${artist.stage_name}-${artist.event_date}`;
      newActivated.add(key);
    });
    setActivatedArtists(newActivated);
  }, [timetable]);

  // Seed history once assignments first arrive (e.g. loaded from the DB) so the
  // first user edit has a baseline to undo back to.
  useEffect(() => {
    if (assignments.length > 0 && history.stack.length === 1 && history.stack[0].length === 0) {
      setHistory(initHistory(assignments));
    }
  }, [assignments.length === 0]);

  // Save to history whenever assignments update. Tag any brand-new block with a
  // stable client id here — the single choke point every edit flows through —
  // so resize/delete/undo can always re-find it and no creation site can forget
  // (withClientId is a no-op once a block has an id/__clientId).
  const saveToHistory = (newAssignments: Assignment[]) => {
    const tagged = newAssignments.map(withClientId);
    setHistory((h) => pushHistory(h, tagged));
    onAssignmentsUpdate(tagged);
  };

  // Handle AI-generated assignments
  const handleAiAssignmentsGenerated = (newAssignments: Assignment[]) => {
    // Clear existing assignments for selected date first, then add new ones.
    // saveToHistory tags each new block with a client id so the producer can
    // resize/delete/undo them like any hand-placed block.
    const otherDayAssignments = assignments.filter((a) => a.event_date !== selectedDate);
    const mergedAssignments = [...otherDayAssignments, ...newAssignments];
    saveToHistory(mergedAssignments);
    // No need to set hasGeneratedWithAi - hasAssignmentsForSelectedDate is computed dynamically

    // Keep all artists activated - don't deactivate any

    // Reset auto-generate flag
    setAutoGenerate(false);
  };

  const handleAiSettingsSaved = (settings: AiGenerationSettings) => {
    setPreviousAiSettings(settings);

    // Sync custom subjects back to the briefing so they persist on save
    if (onCustomSubjectsUpdate && settings.customSubjects?.length) {
      const mapped = settings.customSubjects.map((cs) => ({
        name: cs.subject,
        label: cs.subject,
        location: cs.location || undefined,
      }));
      onCustomSubjectsUpdate(mapped);
    }
  };

  // Clear assignments without resetting hasGeneratedWithAi flag
  const clearAssignmentsForDate = () => {
    const filtered = assignments.filter((a) => a.event_date !== selectedDate);
    saveToHistory(filtered);
  };

  const handleRegenerateWithPrevious = async () => {
    // Don't clear here - handleAiAssignmentsGenerated will replace assignments
    setAutoGenerate(true);
    setAiRegenerateDialogOpen(false);
    setAiDialogOpen(true);
  };

  const handleRegenerateWithNew = () => {
    // Don't clear here - handleAiAssignmentsGenerated will replace assignments
    setAiRegenerateDialogOpen(false);
    setAutoGenerate(false);
    setAiDialogOpen(true);
  };

  const handleClearGenerated = () => {
    // Remove only assignments for the selected date
    // hasAssignmentsForSelectedDate updates automatically since it's computed
    clearAssignmentsForDate();
  };

  const handleRegenerateWithInstructions = async (instructions: string) => {
    setAiRegenerateDialogOpen(false);
    const toastId = toast.loading("AI is modifying the schedule based on your instructions...");

    try {
      // Get current assignments for the selected date
      const currentDateAssignments = assignments.filter((a) => a.event_date === selectedDate);

      // Use previous AI settings or defaults
      const slotMinutes = previousAiSettings?.slotMinutes || 15;
      const defaultDuration = previousAiSettings?.defaultDuration || 15;
      const crewBreaks = previousAiSettings?.crewBreaks || [];
      const customSubjects = previousAiSettings?.customSubjects || [];
      const travelTime = previousAiSettings?.travelTime || 5;

      // Preserve the crew stage preferences the producer set in the AI
      // window (same split the dialog uses: "no_stage" → noStage flag).
      const preferredStagesFor = previousAiSettings?.crewPreferredStages ?? {};

      // Prepare crew data — carry preferredStages / noStage so the
      // regenerate path respects the same settings as first generation.
      const crewData = crewForDate
        .filter((c): c is typeof c & { id: string } => !!c.id)
        .map((c) => {
          const prefs = preferredStagesFor[c.id] || [];
          return {
            id: c.id,
            name: c.name,
            function: c.function,
            preferredStages: prefs.filter((s) => s !== "no_stage"),
            noStage: prefs.includes("no_stage"),
          };
        });

      // Prepare artists data - map to correct field names expected by edge function
      const artistsData = artistsForDate.map((a) => ({
        name: a.artist_name,
        stage: a.stage_name,
        start: a.set_start,
        end: a.set_end,
      }));

      const { data, error } = await supabase.functions.invoke("generate-crew-schedule", {
        body: buildGenerateScheduleBody({
          briefingId,
          date: selectedDate,
          slotMinutes,
          defaultDuration,
          travelTime,
          crew: crewData,
          artists: artistsData,
          // The canonical day stages — previously missing here, which made
          // the server unable to resolve any stage on regenerate.
          briefingStages: stagesForSelectedDate.map((s) => ({ id: s.id, label: s.label })),
          customSubjects,
          crewBreaks: crewBreaks.map((b: { time: string; duration: string | number }) => ({
            time: b.time,
            duration: typeof b.duration === "string" ? parseInt(b.duration, 10) : b.duration,
          })),
          existingAssignments: currentDateAssignments,
          regenerateInstructions: instructions,
        }),
      });

      if (error) {
        console.error(error);
        toast.error("Failed to regenerate schedule", { id: toastId });
        return;
      }

      if (data?.assignments && Array.isArray(data.assignments)) {
        // Validate that all crew_member_ids exist in the crew list
        const validCrewIds = new Set(
          crewForDate.map((c) => c.id).filter((id): id is string => !!id),
        );
        const validAssignments = data.assignments.filter((a: Assignment) =>
          validCrewIds.has(a.crew_member_id),
        );

        // Log any invalid assignments for debugging
        const invalidAssignments = data.assignments.filter(
          (a: Assignment) => !validCrewIds.has(a.crew_member_id),
        );
        if (invalidAssignments.length > 0) {
          toast.warning(
            `${invalidAssignments.length} assignment(s) were skipped due to invalid crew references`,
          );
        }

        // Process and apply the valid assignments (saveToHistory tags each).
        const newAssignments = validAssignments.map((a: Assignment) => ({
          ...a,
          briefing_id: briefingId,
          event_date: selectedDate,
        }));

        // Replace assignments for selected date with new ones
        const otherDayAssignments = assignments.filter((a) => a.event_date !== selectedDate);
        const mergedAssignments = [...otherDayAssignments, ...newAssignments];
        saveToHistory(mergedAssignments);
        // hasAssignmentsForSelectedDate updates automatically

        toast.success("Schedule modified successfully", { id: toastId });
      } else if (data?.error) {
        toast.error(data.error, { id: toastId });
      } else {
        toast.error("No assignments were generated", { id: toastId });
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while modifying the schedule", { id: toastId });
    }
  };

  // Undo / redo. Both step the shared history cursor and push the resulting
  // snapshot up to the parent — the timeline re-renders from that state.
  const canUndo = historyCanUndo(history);
  const canRedo = historyCanRedo(history);

  const handleUndo = () => {
    if (!historyCanUndo(history)) return;
    const { history: next, value } = undoHistory(history);
    setHistory(next);
    onAssignmentsUpdate(value);
  };

  const handleRedo = () => {
    if (!historyCanRedo(history)) return;
    const { history: next, value } = redoHistory(history);
    setHistory(next);
    onAssignmentsUpdate(value);
  };

  // Get artists for selected date
  const artistsForDate = timetable.filter((a) => a.event_date === selectedDate);

  // See the note near the top: resolve every artist's photo URL, download the
  // bytes only for the day being looked at.
  useArtistImagePrefetch(
    useMemo(() => timetable.map((a) => a.artist_name), [timetable]),
    useMemo(() => artistsForDate.map((a) => a.artist_name), [artistsForDate]),
  );

  // Get crew for selected date
  const crewForDate = crew.filter((c) => c.event_date === selectedDate);

  // Initialize activated crew - activate all by default
  useEffect(() => {
    if (crewForDate.length > 0) {
      const crewIds = new Set<string>();
      crewForDate.forEach((member) => {
        if (member.id) crewIds.add(member.id);
      });
      // Only update if the set has changed
      if (
        crewIds.size !== activatedCrew.size ||
        !Array.from(crewIds).every((id) => activatedCrew.has(id))
      ) {
        setActivatedCrew(crewIds);
      }
    }
  }, [selectedDate, crew.length, crewForDate.map((c) => c.id).join(",")]);

  // Get activated crew for selected date
  const activeCrewForDate = crewForDate.filter((c) => activatedCrew.has(c.id || ""));
  // "assist"-role crew of the selected day — the candidate assistants, computed
  // once and reused by the legend dropdown and the per-slot dialog dropdown.
  const assistantCrewForDate = activeCrewForDate.filter((c) => c.id && isAssistantRole(c.function));

  // Get filtered crew based on selection and hidden state.
  // Uses the shared visibility predicate so custom rows and artist
  // assignments react identically to the eye toggle + "Show assigned" filter.
  const filteredCrewForDate = activeCrewForDate.filter((c) =>
    isCrewMemberVisible(c.id || "", { hiddenCrew, selectedCrewFilter }),
  );

  // The "Drag to assign" list has its own function filter and sort order, so
  // a producer can narrow a big crew down to the roles they're scheduling
  // right now. Deliberately separate from the timeline's own filters: this
  // only reorders/narrows the pick list, never the schedule itself.
  const crewListForPicking = sortCrew(
    applyFunctionFilter(activeCrewForDate, crewListFunctions),
    crewListSort,
  );

  // Stages to render for the selected day — every stage configured for
  // the day (incl. ones with no artists yet) plus any lineup-only
  // orphan, deduped + ordered. See resolveScheduleStages for the rules.
  const stagesForDate = resolveScheduleStages({
    dayStages: stagesForSelectedDate,
    allStages: stages,
    briefingStages,
    artists: artistsForDate,
  });
  // Stages actually rendered after the (optional) "filter by stage" UI.
  // Filtering only affects which stage rows are shown; the underlying
  // assignment/lineup data is untouched.
  const stagesToDisplay = applyStageFilter(stagesForDate, selectedStages);

  // Stage ids configured for the selected day. A custom assignment bound
  // to one of these renders inside that stage's row (kept out of the
  // per-crew "Custom" row to avoid showing the same block twice).
  const dayStageIdSet = new Set(stagesForSelectedDate.map((s) => s.id));

  // 24-hour timeline anchored on the festival's doors-open time for the
  // selected day (falls back to 09:00 when doors aren't set). Items placed
  // before doors pull the start back so nothing disappears. Shared with the
  // read-only schedule view via resolveScheduleStartHour.
  const startHour = resolveScheduleStartHour({
    doorsOpen: festivalDays.find((d) => d.date === selectedDate)?.startTime,
    itemStartTimes: [
      ...artistsForDate.map((a) => a.set_start),
      ...assignments.filter((a) => a.event_date === selectedDate).map((a) => a.filming_start),
    ],
    fallback: 9,
  });
  const endHour = startHour + 24;
  // Total scroll-canvas width: sticky label column + 24h grid.
  const timelineWidth = labelWidth + (endHour - startHour) * HOUR_WIDTH * zoomLevel;

  // Convert time to pixels for positioning (shared with the read-only timeline
  // and the current-time marker, so blocks and marker can never drift apart).
  const timeToPosition = (time: string) =>
    horizontalPositionForTime(time, startHour, HOUR_WIDTH * zoomLevel);

  // Feedback dots for one row, mirrored onto the editable schedule. z-20 keeps
  // them above assignment blocks but below the z-30 sticky stage label.
  const renderRowFeedback = (label: string) =>
    (feedbackByRow.get(label) ?? []).map((m) => (
      <ScheduleFeedbackMarker
        formatTime={formatTime}
        key={m.id}
        marker={m}
        left={timeToPosition(m.anchor_time!)}
        zClass="z-20"
        ringClass="ring-card"
        onResolve={resolveFeedback}
        onDelete={removeFeedback}
      />
    ));

  // Live "now" marker. Derived from the CURRENT startHour/zoom on every render
  // (see useCurrentTimeMarker) — startHour is resolved from doors-open + the
  // day's items, which load asynchronously, so a position snapshotted once on
  // mount used to leave the marker a whole hour off the grid.
  const currentTimeMarker = useCurrentTimeMarker({
    startHour,
    pixelsPerHour: HOUR_WIDTH * zoomLevel,
    spanHours: endHour - startHour,
    enabled: isToday && producerSettings?.schedule_show_current_time_marker !== false,
  });

  // Calculate duration in pixels
  const getDuration = (start: string, end: string) => {
    return timeToPosition(end) - timeToPosition(start);
  };

  // Convert pixel position to time
  const positionToTime = (position: number) => {
    const totalMinutes = Math.round((position / (60 * zoomLevel)) * 15);
    let hours = startHour + Math.floor(totalMinutes / 60);
    const minutes = Math.floor((totalMinutes % 60) / 15) * 15; // Snap to 15min

    // Convert back to 24-hour clock (startHour can be any hour, incl. > 23
    // when items roll past midnight, so wrap with modulo rather than a single
    // subtraction).
    hours = ((hours % 24) + 24) % 24;

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  };

  // Check if artist is activated
  const isArtistActivated = (artist: Artist) => {
    const key = `${artist.artist_name}-${artist.stage_name}-${artist.event_date}`;
    return activatedArtists.has(key);
  };

  // True when an assignment refers to this artist's slot. FK-first
  // via lineup_id (post-migration 20260504240200); falls back to the
  // (artist_name, stage_name) pair for legacy rows. Stage-id alone
  // is not sufficient because a stage hosts multiple artists.
  const assignmentMatchesArtist = (a: Assignment, artist: Artist): boolean => {
    if (a.lineup_id && artist.id) return a.lineup_id === artist.id;
    return a.artist_name === artist.artist_name && a.stage_name === artist.stage_name;
  };

  // Get assignments for a specific artist (filter out invalid crew_member_ids).
  // Includes derived mirror PREVIEW blocks so an assistant's filming slots show
  // up (and stack) alongside the assisted member's.
  const getArtistAssignments = (artist: Artist) => {
    // Mirrors last (assistant sits below the filming member), then a stable
    // alphabetical order — see sortStackedAssignments.
    return sortStackedAssignments(
      assignmentsWithMirrors.filter(
        (a) =>
          assignmentMatchesArtist(a, artist) &&
          a.assignment_type === "artist" &&
          crewForDate.some((c) => c.id === a.crew_member_id),
      ),
      (a) => !!a.__mirror,
      (crewId) => crewForDate.find((c) => c.id === crewId)?.name ?? "",
    );
  };

  // Get crew member by ID
  const getCrewMember = (crewId: string) => {
    return crewForDate.find((c) => c.id === crewId);
  };

  // Open artist detail dialog. `clickTime` (HH:MM) is the point on the timeline
  // the producer clicked; for a brand-new assignment the block starts there
  // (clamped into the set) instead of always at the set start.
  const handleArtistClick = (artist: Artist, assignment?: Assignment, clickTime?: string) => {
    if (justFinishedResizing) return; // Prevent opening after resize

    setSelectedArtist(artist);

    if (assignment) {
      // Edit specific assignment
      setSelectedAssignmentForEdit(assignment);
      setFilmingStart(assignment.filming_start);
      setFilmingEnd(assignment.filming_end);
      setArtistNotes(assignment.notes || "");
    } else {
      const existingAssignments = getArtistAssignments(artist);
      if (existingAssignments.length > 0) {
        const firstAssignment = existingAssignments[0];
        setSelectedAssignmentForEdit(firstAssignment);
        setFilmingStart(firstAssignment.filming_start);
        setFilmingEnd(firstAssignment.filming_end);
        setArtistNotes(firstAssignment.notes || "");
      } else {
        // New assignment: a block of the producer's chosen length (not a
        // hardcoded 15 min). It starts where the producer clicked on the
        // timeline, or at the artist's real set start when opened without a
        // click, and is fitted inside the set so it never runs past the set
        // end (fitBlockIntoSet is shared with the drag/drop path).
        setSelectedAssignmentForEdit(null);
        const desiredStart = clickTime ?? normalizeTime(artist.set_start);
        const { start, end } = fitBlockIntoSet(
          desiredStart,
          defaultAssignmentLength,
          artist.set_start,
          artist.set_end,
          startHour,
        );
        setFilmingStart(start);
        setFilmingEnd(end);
        setArtistNotes("");
      }
    }
    setDetailDialogOpen(true);
  };

  // Save artist assignment
  const handleSaveArtistAssignment = (crewMemberId: string) => {
    if (!selectedArtist) return;

    // Resolve stage_id from briefingStages so the new assignment carries
    // the canonical FK from the start. Trigger crew_assignments_set_stage_id
    // covers the NULL fallback, but explicit attach avoids a round-trip
    // and keeps React state aligned with what hits the DB.
    const start = normalizeTime(filmingStart);
    const end = normalizeTime(filmingEnd);

    // A crew member can't film two things at once: block a placement that would
    // double-book them (e.g. already on stage A at 20:00, now added to a
    // whole-festival stage-B artist at 20:00). Their existing block for THIS
    // artist is ignored because it's about to be replaced below.
    if (
      crewMemberHasConflict(assignments, {
        crewMemberId,
        eventDate: selectedDate,
        start,
        end,
        ignore: (a) => assignmentMatchesArtist(a, selectedArtist),
      })
    ) {
      toast.error(CREW_CONFLICT_MESSAGE);
      return;
    }

    const stageId = briefingStages.find((s) => s.label === selectedArtist.stage_name)?.id ?? null;

    const newAssignment: Assignment = withClientId({
      crew_member_id: crewMemberId,
      event_date: selectedDate,
      artist_name: selectedArtist.artist_name,
      stage_name: selectedArtist.stage_name,
      stage_id: stageId,
      filming_start: start,
      filming_end: end,
      assignment_type: "artist",
      notes: artistNotes,
    });

    // Remove existing assignment for this crew member and artist
    const filtered = assignments.filter(
      (a) => !(a.crew_member_id === crewMemberId && assignmentMatchesArtist(a, selectedArtist)),
    );

    saveToHistory([...filtered, newAssignment]);
    // Keep the dialog editing THIS block, so adjusting the start/end afterwards
    // applies to it (fixes "select crew then set duration → stuck at default").
    setSelectedAssignmentForEdit(newAssignment);
  };

  // While the detail dialog is open and editing a placed block, keep that
  // block's times in sync with the start/end inputs so the block on the
  // timeline grows/shrinks live. No per-keystroke history entry — the
  // placement already recorded one; undo removes the whole placement.
  useEffect(() => {
    if (!detailDialogOpen || !selectedAssignmentForEdit) return;
    if (!filmingStart || !filmingEnd) return;
    const start = normalizeTime(filmingStart);
    const end = normalizeTime(filmingEnd);
    const target = selectedAssignmentForEdit;
    // Normalise BOTH sides: DB-loaded blocks carry seconds ("20:05:00"), so a
    // raw compare would report a change on open (stripping seconds) and push a
    // spurious state update just from inspecting a block. Only propagate a real
    // time edit.
    if (normalizeTime(target.filming_start) === start && normalizeTime(target.filming_end) === end)
      return;
    // Single pass: apply the new times to the matching block, noting whether we
    // touched anything so we skip the update (and re-render) when the block
    // isn't in state yet (no crew assigned).
    let changed = false;
    const next = assignments.map((a) => {
      if (!assignmentsMatch(a, target)) return a;
      changed = true;
      return { ...a, filming_start: start, filming_end: end };
    });
    if (!changed) return;
    onAssignmentsUpdate(next);
    setSelectedAssignmentForEdit({ ...target, filming_start: start, filming_end: end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filmingStart, filmingEnd, detailDialogOpen]);

  // Handle custom subject creation
  const handleCreateCustomSubject = () => {
    if (!selectedCrewForCustom || !customSubject.trim() || !customStart || !customEnd) {
      toast.error("Please fill in all required fields (Subject, Start time, End time)");
      return false; // Return false to indicate validation failure
    }

    const selectedStage = customStageId
      ? (briefingStages.find((s) => s.id === customStageId) ?? null)
      : null;

    const newAssignment: Assignment = {
      crew_member_id: selectedCrewForCustom,
      event_date: selectedDate,
      stage_id: selectedStage?.id ?? null,
      stage_name: selectedStage?.label ?? "Other",
      filming_start: normalizeTime(customStart),
      filming_end: normalizeTime(customEnd),
      assignment_type: "custom",
      subject: customSubject,
      location: customLocation || undefined,
      notes: customNotes,
    };

    saveToHistory([...assignments, newAssignment]);
    setCustomSubject("");
    setCustomLocation("");
    setCustomStageId(null);
    setCustomNotes("");
    setCustomStart("");
    setCustomEnd("");
    setSelectedCrewForCustom("");
    return true; // Return true to indicate success
  };

  // Save the custom dialog for an EXISTING assignment. Shared by the Save
  // button and the Enter shortcut. Also the commit point of the
  // drag-to-strip flow: that flow opens this dialog without moving anything,
  // so an artist-bound assignment is converted to a stage-bound custom only
  // here — closing the dialog instead leaves it untouched. Returns true when
  // saved (callers close the dialog on true).
  const handleUpdateCustomSubject = (): boolean => {
    if (!selectedAssignmentForEdit) return false;
    if (!customSubject.trim() || !customStart || !customEnd) {
      toast.error("Please fill in all required fields (Subject, Start time, End time)");
      return false;
    }
    const targetCrewId = selectedCrewForCustom || selectedAssignmentForEdit.crew_member_id;
    const start = normalizeTime(customStart);
    const end = normalizeTime(customEnd);
    // Only guard against overlaps when the crew or the times actually
    // changed: the create path never blocked overlapping customs, so a
    // subject-only edit of an existing (possibly overlapping) duty must
    // keep working.
    const placementChanged =
      targetCrewId !== selectedAssignmentForEdit.crew_member_id ||
      start !== normalizeTime(selectedAssignmentForEdit.filming_start) ||
      end !== normalizeTime(selectedAssignmentForEdit.filming_end);
    if (placementChanged && checkOverlap(targetCrewId, start, end, selectedAssignmentForEdit)) {
      toast.error("Cannot place here - overlaps with another assignment");
      return false;
    }
    const selectedStage = customStageId
      ? (briefingStages.find((s) => s.id === customStageId) ?? null)
      : null;
    const filtered = assignments.filter((a) => !assignmentsMatch(a, selectedAssignmentForEdit));
    // The stage/no-stage binding goes through the same rebind helper the
    // paste path uses, so the artist→custom conversion field set lives in
    // one place (lib/assignmentClipboard.ts).
    const rebound = selectedStage
      ? rebindToStage(selectedAssignmentForEdit, {
          id: selectedStage.id,
          label: selectedStage.label,
        })
      : {
          ...rebindToStage(selectedAssignmentForEdit, { id: "", label: "Other" }),
          stage_id: null,
        };
    const updated: Assignment = {
      ...rebound,
      crew_member_id: targetCrewId,
      subject: customSubject,
      location: customLocation || undefined,
      notes: customNotes,
      filming_start: start,
      filming_end: end,
    };
    // Route through saveToHistory (like the create + delete paths) so this
    // edit is undoable — a direct onAssignmentsUpdate would leave it out of
    // the history stack.
    saveToHistory([...filtered, updated]);
    return true;
  };

  // Single save path for the custom dialog (Enter shortcut + Save button):
  // create when no assignment is being edited, update otherwise; close only
  // on success so validation errors keep the dialog open.
  const saveCustomDialog = () => {
    const success = selectedAssignmentForEdit
      ? handleUpdateCustomSubject()
      : handleCreateCustomSubject();
    if (success) {
      setCustomDialogOpen(false);
      setSelectedAssignmentForEdit(null);
    }
  };

  // Adjust custom time by 15 minutes
  const adjustTime = (time: string, direction: "add" | "subtract") => {
    if (!time) return "12:00";
    const [hours, minutes] = time.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes;
    totalMinutes += direction === "add" ? 15 : -15;
    if (totalMinutes < 0) totalMinutes = 0;
    if (totalMinutes >= 1440) totalMinutes = 1439;
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;
  };

  // Handle timeline click for custom subject
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>, crewId: string) => {
    if (draggedCrew || justFinishedResizing || !timelineRef.current) return; // Don't open dialog during drag or after resize
    // A narrow block lets the post-menu stray click retarget onto this row and
    // pop the "new custom subject" dialog; ignore it like the blocks do.
    if (isStrayMenuClick(e)) return;

    // Get the drop target's rect (the timeline grid, not the scrollable container)
    const targetRect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - targetRect.left;
    const clickTime = positionToTime(relativeX);
    const endTime = adjustTime(clickTime, "add");

    // Check for a duplicate/overlapping assignment for this crew member at this
    // time. crewMemberHasConflict compares in minutes so HH:MM (clickTime) and
    // HH:MM:SS (DB-stored filming times) don't false-positive at exact slot
    // boundaries (e.g. a break ending 12:00:00 vs a new slot starting 12:00).
    if (
      crewMemberHasConflict(assignments, {
        crewMemberId: crewId,
        eventDate: selectedDate,
        start: clickTime,
        end: endTime,
      })
    ) {
      toast.error(CREW_CONFLICT_MESSAGE);
      return;
    }

    // Auto-select crew member if only one exists for this date
    const crewToSelect = crewForDate.length === 1 ? crewForDate[0].id || crewId : crewId;

    setSelectedAssignmentForEdit(null);
    setSelectedCrewForCustom(crewToSelect);
    setCustomStart(clickTime);
    setCustomEnd(endTime);
    setCustomSubject("");
    setCustomLocation("");
    setCustomStageId(null);
    setCustomNotes("");
    setCustomDialogOpen(true);
  };

  // Drag and drop handlers
  const handleCrewDragStart = (e: React.DragEvent, crewId: string) => {
    setDraggedCrew(crewId);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleCrewDragEnd = () => {
    setDraggedCrew(null);
  };

  const checkOverlap = (
    crewId: string,
    start: string,
    end: string,
    excludeIdOrAssignment?: string | Assignment,
  ) =>
    crewMemberHasConflict(assignments, {
      crewMemberId: crewId,
      eventDate: selectedDate,
      start,
      end,
      // Exclude the assignment being moved/edited (by ID or by content match)
      // so a no-op reposition doesn't clash with itself.
      ignore: (a) => {
        if (!excludeIdOrAssignment) return false;
        return typeof excludeIdOrAssignment === "string"
          ? a.id === excludeIdOrAssignment
          : assignmentsMatch(a, excludeIdOrAssignment);
      },
    });

  const handleDropOnArtist = (artist: Artist, e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedCrew) return;

    // Check if artist is activated
    if (!isArtistActivated(artist)) {
      toast.error("Cannot assign to deactivated artist. Please activate the artist first.");
      setDraggedCrew(null);
      return;
    }

    const crewMember = getCrewMember(draggedCrew);
    if (!crewMember) return;

    // Get the scroll viewport and calculate the correct position
    const viewport = timelineRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    const scrollLeft = viewport?.scrollLeft || 0;
    const viewportRect = viewport?.getBoundingClientRect();

    if (!viewportRect) return;

    // Calculate position relative to the scrolled content (not the visible viewport)
    const relativeX = e.clientX - viewportRect.left + scrollLeft - labelWidth;

    // Convert position to time. Snaps to the 15-min grid, but the artist's real
    // (possibly off-grid) set start/end are also snap targets so a set like
    // 20:05–21:10 can be landed on exactly.
    const dropTime = positionToSnappedTime(
      relativeX,
      startHour,
      zoomLevel,
      { setStart: artist.set_start, setEnd: artist.set_end },
      "floor",
    );

    // Fit a defaultAssignmentLength block, starting at the drop, inside the set:
    // clamps the start into [set_start, set_end − length] and shortens to the set
    // when it's shorter (10-min set → 10-min block). Timeline-aware, so it also
    // handles midnight-crossing sets (23:30 → 00:30) that a raw clock clamp broke.
    const { start: startTime, end: endTime } = fitBlockIntoSet(
      dropTime,
      defaultAssignmentLength,
      artist.set_start,
      artist.set_end,
      startHour,
    );

    // Check for overlaps with same crew member only
    if (checkOverlap(draggedCrew, startTime, endTime)) {
      toast.error(CREW_CONFLICT_MESSAGE);
      setDraggedCrew(null);
      return;
    }

    // Check if there's an existing assignment at the drop position
    const existingAssignments = assignments.filter(
      (a) => assignmentMatchesArtist(a, artist) && a.assignment_type === "artist",
    );

    // Check if the drop position overlaps with any existing assignment
    const overlappingAssignment = existingAssignments.find((a) =>
      timeRangesOverlap(startTime, endTime, a.filming_start, a.filming_end),
    );

    // If dropping on an existing block with a different crew member, split the block
    let finalStart = startTime;
    let finalEnd = endTime;
    if (overlappingAssignment && overlappingAssignment.crew_member_id !== draggedCrew) {
      // Use the existing assignment's time range to split the block
      finalStart = overlappingAssignment.filming_start;
      finalEnd = overlappingAssignment.filming_end;
    }

    const stageId = briefingStages.find((s) => s.label === artist.stage_name)?.id ?? null;

    const newAssignment: Assignment = {
      crew_member_id: draggedCrew,
      event_date: selectedDate,
      artist_name: artist.artist_name,
      stage_name: artist.stage_name,
      stage_id: stageId,
      filming_start: normalizeTime(finalStart),
      filming_end: normalizeTime(finalEnd),
      assignment_type: "artist",
      notes: "",
    };

    // Keep all existing assignments and add the new one
    const updatedAssignments = [...assignments, newAssignment];
    saveToHistory(updatedAssignments);
    setDraggedCrew(null);
  };

  const handleDropOnCustom = (crewId: string, e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedCrew || draggedCrew !== crewId) return;

    const crewMember = getCrewMember(draggedCrew);
    if (!crewMember) return;

    // Get the scroll viewport and calculate the correct position
    const viewport = timelineRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    const scrollLeft = viewport?.scrollLeft || 0;
    const viewportRect = viewport?.getBoundingClientRect();

    if (!viewportRect) return;

    // Calculate position relative to the scrolled content (not the visible viewport)
    const relativeX = e.clientX - viewportRect.left + scrollLeft - labelWidth;
    const startTime = positionToTime(relativeX);
    const endTime = adjustTime(startTime, "add");

    // Check for overlaps
    if (checkOverlap(draggedCrew, startTime, endTime)) {
      toast.error(CREW_CONFLICT_MESSAGE);
      setDraggedCrew(null);
      return;
    }

    setSelectedCrewForCustom(draggedCrew);
    setCustomStart(startTime);
    setCustomEnd(endTime);
    setDraggedCrew(null);
    setCustomDialogOpen(true);
  };

  // Drop a crew member onto a stage row. If the drop lands inside an
  // artist's set, behave exactly like dropping on that artist. Otherwise
  // (an empty stage, or a gap where no DJ is scheduled yet) open the
  // custom-assignment dialog pre-filled with this stage and time — this is
  // what lets producers schedule crew against a stage that has no lineup
  // yet, e.g. to film the overview / audience.
  const handleDropCrewOnStage = (
    stageRec: BriefingStageRecord | undefined,
    stageArtists: Artist[],
    e: React.DragEvent,
  ) => {
    if (!draggedCrew) return;
    const crewMember = getCrewMember(draggedCrew);
    if (!crewMember) return;

    const rect = e.currentTarget.getBoundingClientRect();
    // Snap to the 15-min grid OR to any set edge on this stage, so a drop right
    // on a set's start (e.g. an artist that begins 18:20) lands exactly there
    // and is recognised as that artist — instead of grid-snapping to 18:15,
    // falling outside the set, and opening the "new custom subject" dialog.
    const setEdges = stageArtists.flatMap((a) => [a.set_start, a.set_end]);
    const dropTime = positionToSnappedTimeMulti(
      e.clientX - rect.left,
      startHour,
      zoomLevel,
      setEdges,
      "floor",
    );

    // Landed on an artist set → reuse the existing artist-drop path.
    const targetArtist = artistAtTime(stageArtists, dropTime);
    if (targetArtist) {
      handleDropOnArtist(targetArtist, e);
      return;
    }

    // No DJ at this time → open the custom dialog bound to this stage.
    const endTime = addMinutesToTime(dropTime, defaultAssignmentLength);

    if (checkOverlap(draggedCrew, dropTime, endTime)) {
      toast.error(CREW_CONFLICT_MESSAGE);
      setDraggedCrew(null);
      return;
    }

    setSelectedCrewForCustom(draggedCrew);
    setCustomStageId(stageRec?.id ?? null);
    setCustomSubject("");
    // Auto-attach the stage's own location; the producer still names the
    // subject of what's being filmed (e.g. "Overview" / "Audience").
    setCustomLocation(stageRec?.location ?? "");
    setCustomNotes("");
    setCustomStart(dropTime);
    setCustomEnd(endTime);
    setDraggedCrew(null);
    setCustomDialogOpen(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedCrew) {
      e.dataTransfer.dropEffect = "copy";
    } else if (activeDraggedAssignment()) {
      e.dataTransfer.dropEffect = "move";
    } else {
      e.dataTransfer.dropEffect = "none";
    }
  };

  // Assignment drag handlers for repositioning
  // Defer the drag STATE update out of the dragstart task. Chrome aborts a
  // native drag instantly when the DOM repaints over the drag source during
  // dragstart — setting draggedAssignment synchronously re-renders the
  // stage-duties drop zone (which overlaps the duty blocks), which made
  // duty blocks impossible to drag while artist blocks (not overlapped)
  // kept working. The ref mirrors the dragged assignment SYNCHRONOUSLY (a
  // ref write causes no repaint) so drop/dragover logic never depends on
  // the deferred state having landed.
  const dragStartTimerRef = useRef<number | null>(null);
  const draggedAssignmentRef = useRef<Assignment | null>(null);
  // The one accessor drop/dragover LOGIC must use (the ref is current even
  // when the deferred state hasn't landed yet); JSX renders keep reading the
  // `draggedAssignment` state.
  const activeDraggedAssignment = () => draggedAssignmentRef.current ?? draggedAssignment;
  // Shared teardown for dragend, successful drops and the strip-to-dialog
  // path. Also cancels a still-pending deferred start (an aborted click-like
  // drag can end before it ran) so drag state can't stick around.
  const clearAssignmentDrag = () => {
    if (dragStartTimerRef.current !== null) {
      window.clearTimeout(dragStartTimerRef.current);
      dragStartTimerRef.current = null;
    }
    draggedAssignmentRef.current = null;
    setDraggedAssignment(null);
    setIsDraggingBlock(false);
  };
  const handleAssignmentDragStart = (e: React.DragEvent, assignment: Assignment) => {
    e.dataTransfer.effectAllowed = "move";
    draggedAssignmentRef.current = assignment;
    dragStartTimerRef.current = window.setTimeout(() => {
      dragStartTimerRef.current = null;
      setDraggedAssignment(assignment);
      setIsDraggingBlock(true);
    }, 0);
  };

  const handleAssignmentDragEnd = () => {
    clearAssignmentDrag();
  };

  // Safety net. `onDragEnd` on the block is the happy path; when the browser
  // ends a drag without dispatching it, these guards clear the state as soon
  // as the pointer moves again — otherwise the whole editor stays in
  // drop-zone mode and ignores every click until the producer reloads.
  useDragSessionGuard(!!draggedAssignment || isDraggingBlock, clearAssignmentDrag);
  useDragSessionGuard(!!draggedCrew, handleCrewDragEnd);

  const handleAssignmentDrop = (
    e: React.DragEvent,
    targetRow: "artist" | "custom" | "stage",
    artist?: Artist,
    crewId?: string,
    stage?: BriefingStageRecord,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const dragged = activeDraggedAssignment();
    if (!dragged) return;

    // Get the scroll viewport and calculate the correct position
    const viewport = timelineRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    const scrollLeft = viewport?.scrollLeft || 0;
    const viewportRect = viewport?.getBoundingClientRect();

    // No viewport = nothing to measure against: abandon the move, but end the
    // drag session so the editor doesn't stay in drop-zone mode.
    if (!viewportRect) {
      clearAssignmentDrag();
      return;
    }

    // Calculate position relative to the scrolled content (not the visible viewport)
    const relativeX = e.clientX - viewportRect.left + scrollLeft - labelWidth;

    // The block keeps its length unless the target artist's set is shorter.
    // Timeline-aware so a block crossing midnight (23:30→00:30) keeps a positive
    // duration instead of collapsing to zero on the move.
    const duration = clockDurationMinutes(dragged.filming_start, dragged.filming_end, startHour);

    let newStartTime: string;
    let newEndTime: string;
    if (targetRow === "artist" && artist) {
      // Snap onto the artist's real (possibly off-grid) start/end — floor so a
      // drop inside a cell keeps its start there — then fit the block inside the
      // set so it never runs past the set end. A 15-min block moved onto a
      // 10-min artist becomes 10 min, matching the drag-to-assign path.
      const snappedStart = positionToSnappedTime(
        relativeX,
        startHour,
        zoomLevel,
        { setStart: artist.set_start, setEnd: artist.set_end },
        "floor",
      );
      const fitted = fitBlockIntoSet(
        snappedStart,
        duration,
        artist.set_start,
        artist.set_end,
        startHour,
      );
      newStartTime = fitted.start;
      newEndTime = fitted.end;
    } else {
      newStartTime = positionToTime(relativeX);
      newEndTime = shiftTime(newStartTime, duration);
    }

    // Determine the target crew member (changes when dropping custom on a different crew row)
    const targetCrewId = targetRow === "custom" && crewId ? crewId : dragged.crew_member_id;

    // Check for overlaps on the TARGET crew member (excluding current assignment)
    if (checkOverlap(targetCrewId, newStartTime, newEndTime, dragged)) {
      toast.error("Cannot move here - overlaps with another assignment");
      clearAssignmentDrag();
      return;
    }

    // Update assignment - keep existing type unless explicitly changing rows
    let updatedAssignment: Assignment = {
      ...dragged,
      crew_member_id: targetCrewId,
      filming_start: newStartTime,
      filming_end: newEndTime,
    };

    // Rebinding to the target row goes through the same helpers the paste
    // path uses (lib/assignmentClipboard), so a move and a paste can't
    // disagree about what "bind to this artist / stage" means — and, above
    // all, so `lineup_id` follows along. It used to be left behind, still
    // pointing at the lineup slot the block came from, while the row render
    // matches on lineup_id FIRST (assignmentMatchesArtist): a block dragged
    // onto another artist kept rendering under its old one, so the move
    // looked like it had done nothing at all.
    if (targetRow === "artist" && artist) {
      // stageIdForLabel is day-scoped: labels repeat across festival days, and
      // an unscoped find would attach another day's stage_id (ghost duty on
      // the wrong day). The DB trigger crew_assignments_set_stage_id only
      // fills NULLs, so a stale UUID would never hit that safety net.
      updatedAssignment = rebindToArtist(updatedAssignment, artistBinding(artist, stageIdForLabel));
    } else if (targetRow === "custom" && crewId) {
      // A crew member's own custom row: no artist and no lineup slot left.
      updatedAssignment.artist_name = undefined;
      updatedAssignment.lineup_id = null;
      // Preserve original stage_name as location, or mark as Custom if no valid stage
      if (!dragged.stage_name || dragged.stage_name.startsWith("Custom")) {
        updatedAssignment.stage_name = "Custom";
        updatedAssignment.stage_id = null;
      } else {
        // Real stage name preserved — keep stage_id in sync (day-scoped:
        // labels repeat across festival days).
        updatedAssignment.stage_id =
          stageIdForLabel(updatedAssignment.stage_name) ?? updatedAssignment.stage_id ?? null;
      }
      updatedAssignment.assignment_type = "custom";
    } else if (targetRow === "stage" && stage) {
      // Dropped a stage-bound custom on a different stage's empty area —
      // rebind it to the target stage (keeps it in that stage's row).
      updatedAssignment = rebindToStage(updatedAssignment, {
        id: stage.id,
        label: stage.label,
      });
    }

    const filtered = assignments.filter((a) => !assignmentsMatch(a, dragged));
    saveToHistory([...filtered, updatedAssignment]);
    clearAssignmentDrag();
  };

  // Handle resize start
  const handleResizeStart = (
    e: React.MouseEvent,
    assignment: Assignment,
    edge: "start" | "end",
  ) => {
    e.stopPropagation();
    e.preventDefault();
    // Store both the working copy and the original for finding it later
    setResizingAssignment({
      assignment: { ...assignment },
      originalAssignment: { ...assignment },
      edge,
    });
  };

  // Handle resize move
  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingAssignment || !timelineRef.current) return;

    // Get the viewport and scroll position
    const viewport = timelineRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    const scrollLeft = viewport?.scrollLeft || 0;
    const viewportRect = viewport?.getBoundingClientRect();

    if (!viewportRect) return;

    // Calculate position: account for viewport offset, label width, and scroll
    const relativeX = e.clientX - viewportRect.left + scrollLeft - labelWidth;

    const { assignment, edge } = resizingAssignment;

    // Clamp to artist boundaries for artist-type assignments. FK
    // first via lineup_id (post-migration 20260504240200) so a
    // producer rename doesn't strand the resize, fall back to text
    // triple for legacy rows.
    let bounds: { setStart: string; setEnd: string } | null = null;
    if (assignment.assignment_type === "artist" && assignment.artist_name) {
      const artist = timetable.find((a) => {
        if (assignment.lineup_id && a.id) return assignment.lineup_id === a.id;
        return (
          a.artist_name === assignment.artist_name &&
          a.stage_name === assignment.stage_name &&
          a.event_date === assignment.event_date
        );
      });
      if (artist) bounds = { setStart: artist.set_start, setEnd: artist.set_end };
    }

    // Snap to the 15-min grid, but let the artist's real set start/end (which
    // may be off-grid, e.g. 20:05 / 21:10) be snap targets too — otherwise a
    // producer could never drag the edge to the actual start or end of the set.
    const newTime = positionToSnappedTime(relativeX, startHour, zoomLevel, bounds);

    // Resolve the new edge time in timeline-minutes so midnight-crossing sets
    // (e.g. 23:00→01:00) resize correctly; null = move rejected (too short or
    // past the opposite edge).
    const clampedTime = resolveResizeTime({
      targetTime: newTime,
      edge,
      oppositeTime: edge === "start" ? assignment.filming_end : assignment.filming_start,
      bounds,
      fallbackHour: startHour,
    });
    if (clampedTime === null) return;

    setResizingAssignment({
      edge,
      originalAssignment: resizingAssignment.originalAssignment,
      assignment: {
        ...assignment,
        ...(edge === "start" ? { filming_start: clampedTime } : { filming_end: clampedTime }),
      },
    });
  };

  // Match two blocks as the same assignment (stable id → client id → field
  // tuple). See lib/assignmentIdentity; aliased for the many local call sites.
  const assignmentsMatch = matchAssignments;

  // While an assignment's edge is being dragged the live working copy lives
  // in resizingAssignment; rendering through this helper makes the block
  // track the drag continuously instead of jumping on mouse-up.
  const liveTimesFor = (assignment: Assignment) =>
    getEffectiveAssignmentTimes(assignment, resizingAssignment, assignmentsMatch);

  // Handle resize end
  const handleResizeEnd = () => {
    if (resizingAssignment) {
      const { assignment, originalAssignment } = resizingAssignment;

      // Check for overlaps with other assignments for the same crew member on
      // the same day, ignoring the block being resized (matched on its original
      // times).
      const hasOverlap = crewMemberHasConflict(assignments, {
        crewMemberId: assignment.crew_member_id,
        eventDate: assignment.event_date,
        start: assignment.filming_start,
        end: assignment.filming_end,
        ignore: (a) => assignmentsMatch(a, originalAssignment),
      });

      if (hasOverlap) {
        toast.error("Time conflict: this crew member is already assigned during that period", {
          description: "Adjust the start/end time or choose another 15-minute slot.",
        });
        setResizingAssignment(null);
        setJustFinishedResizing(true);
        setTimeout(() => setJustFinishedResizing(false), 100);
        return;
      }

      // Find the exact assignment using the original times
      const existingIndex = assignments.findIndex((a) => assignmentsMatch(a, originalAssignment));

      if (existingIndex !== -1) {
        // Replace the assignment at the found index
        const updated = [...assignments];
        updated[existingIndex] = { ...assignment };
        // Use saveToHistory so each resize is properly tracked for undo
        saveToHistory(updated);
      }
    }
    setResizingAssignment(null);
    setJustFinishedResizing(true);
    setTimeout(() => setJustFinishedResizing(false), 100);
  };

  // Add event listeners for resize
  useEffect(() => {
    if (resizingAssignment) {
      // Add CSS to prevent text selection during resize but allow pointer events
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);

      return () => {
        // Clean up styles
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [resizingAssignment]);

  // ── Copy / cut / paste ────────────────────────────────────────────────
  // Snapped "HH:MM" from a click relative to a custom row element. Mirrors
  // handleTimelineClick — the math is relative to that row, no scroll juggling.
  const rowClickToTime = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return positionToTime(e.clientX - rect.left);
  };

  // Snapped "HH:MM" from an absolute cursor X anywhere over the timeline.
  // Mirrors handleAssignmentDrop's viewport+scroll math, needed for the artist
  // rows whose blocks aren't full-width row elements.
  const viewportClientXToTime = (clientX: number) => {
    const viewport = resolveScrollViewport();
    const viewportRect = viewport?.getBoundingClientRect();
    if (!viewportRect) return positionToTime(0);
    const scrollLeft = viewport?.scrollLeft || 0;
    return positionToTime(clientX - viewportRect.left + scrollLeft - labelWidth);
  };

  const setCrewAnchor = (e: React.MouseEvent<HTMLElement>, crewId: string) => {
    if (!crewId) return;
    pasteAnchorRef.current = { kind: "crew", crewMemberId: crewId, startTime: rowClickToTime(e) };
  };

  const setArtistAnchor = (e: React.MouseEvent<HTMLElement>, artist: Artist) => {
    pasteAnchorRef.current = {
      kind: "artist",
      artist,
      startTime: viewportClientXToTime(e.clientX),
    };
  };

  const setStageAnchor = (e: React.MouseEvent<HTMLElement>, stage: BriefingStageRecord) => {
    pasteAnchorRef.current = {
      kind: "stage",
      stage,
      startTime: viewportClientXToTime(e.clientX),
    };
  };

  // Resolve a canonical stage_id from its label (briefing_stages), scoped to the
  // selected day — labels repeat across days, so an unscoped label match could
  // return another day's stage_id (same wrong-day defect as the stage-row
  // lookup below). Used by the paste/rebind flow, which is always day-scoped.
  const stageIdForLabel = (label?: string) =>
    (label && briefingStages.find((s) => s.event_date === selectedDate && s.label === label)?.id) ||
    null;

  // Place `toPlace` on `crewMemberId` at `startTime`, honouring overlap rules
  // and (for "cut") removing the original `source`. Returns true when it landed.
  const commitPaste = (
    source: Assignment,
    toPlace: Assignment,
    crewMemberId: string,
    startTime: string,
    mode: "copy" | "cut",
  ): boolean => {
    // Keep an artist binding only when that artist plays the selected day,
    // otherwise drop to a custom block so the paste stays visible.
    const artistOnDay =
      toPlace.assignment_type === "artist" &&
      artistsForDate.some(
        (art) => art.artist_name === toPlace.artist_name && art.stage_name === toPlace.stage_name,
      );
    const convertToCustom = toPlace.assignment_type === "artist" && !artistOnDay;

    const pasted = rebaseAssignment(
      toPlace,
      { crewMemberId, startTime, eventDate: selectedDate },
      { convertToCustom },
    );

    // An artist assignment must never run past the artist's set. Pull the end
    // back to the set end (and warn) rather than letting it spill over into the
    // next artist while still carrying the first artist's name.
    if (pasted.assignment_type === "artist") {
      const slot = artistsForDate.find(
        (art) => art.artist_name === pasted.artist_name && art.stage_name === pasted.stage_name,
      );
      if (slot) {
        const { end, shortened, impossible } = clampEndToSetEnd(
          pasted.filming_start,
          pasted.filming_end,
          slot.set_end,
        );
        if (impossible) {
          toast.error(`Can't paste here — ${slot.artist_name}'s set has already ended.`);
          return false;
        }
        if (shortened) {
          pasted.filming_end = end;
          toast.warning(
            `Assignment shortened to ${slot.artist_name}'s set end (${formatTime(slot.set_end)}).`,
          );
        }
      }
    }

    if (
      checkOverlap(
        crewMemberId,
        pasted.filming_start,
        pasted.filming_end,
        mode === "cut" ? source : undefined,
      )
    ) {
      toast.error("Can't paste here — overlaps another assignment for this crew member.");
      return false;
    }

    const base =
      mode === "cut" ? assignments.filter((a) => !assignmentsMatch(a, source)) : assignments;
    // A paste is a brand-new block: strip the source's transient client id
    // (rebaseAssignment drops the DB id but the __clientId rides along on the
    // spread) so saveToHistory mints a fresh one and it can't be confused with
    // the block it was copied from.
    const placed = { ...pasted, __clientId: undefined };
    saveToHistory([...base, placed]);
    return true;
  };

  // Toast policy for assignment edits: placing, moving, duplicating and
  // deleting a block say nothing — the block itself is the confirmation, and
  // every refusal still raises its own error toast. Copy and cut DO toast:
  // nothing changes on screen, so without one there is no way to tell the
  // clipboard picked the block up.
  const clearClipboardAfterCut = (mode: "copy" | "cut") => {
    if (mode === "cut") clipboard.clear();
  };

  const handleCopyAssignment = (assignment: Assignment) => {
    suppressNextClick();
    clipboard.copy(assignment);
    toast.success("Assignment copied");
  };

  const handleCutAssignment = (assignment: Assignment) => {
    suppressNextClick();
    clipboard.cut(assignment);
    toast("Assignment cut — paste it somewhere to move it");
  };

  const handleDuplicateAssignment = (assignment: Assignment) => {
    suppressNextClick();
    // Drop a copy immediately after the block on the same crew row.
    commitPaste(
      assignment,
      assignment,
      assignment.crew_member_id,
      normalizeTime(assignment.filming_end),
      "copy",
    );
  };

  const handleDeleteAssignment = (assignment: Assignment) => {
    suppressNextClick();
    // Let the block shrink/fade out first, then remove it from state. The commit
    // runs ~160ms later and filters from the LATEST state (assignmentsRef), so a
    // second delete — or any edit — that lands in the window isn't clobbered by a
    // stale snapshot.
    blockRemoval.remove(assignmentStableId(assignment), () => {
      saveToHistory(assignmentsRef.current.filter((a) => !assignmentsMatch(a, assignment)));
      if (clipboard.entry && assignmentsMatch(clipboard.entry.assignment, assignment)) {
        clipboard.clear();
      }
    });
  };

  // Relocate the clipboard block onto a crew member's row, keeping its binding.
  const pasteOntoCrew = (crewMemberId: string, startTime: string) => {
    suppressNextClick();
    const entry = clipboard.entry;
    if (!entry) return;
    if (commitPaste(entry.assignment, entry.assignment, crewMemberId, startTime, entry.mode)) {
      clearClipboardAfterCut(entry.mode);
    }
  };

  // Paste the clipboard block onto an artist slot: re-bind it to that artist,
  // keep the source crew member, position at `startTime`.
  const pasteOntoArtist = (artist: Artist, startTime: string) => {
    suppressNextClick();
    const entry = clipboard.entry;
    if (!entry) return;
    const rebound = rebindToArtist(entry.assignment, artistBinding(artist, stageIdForLabel));
    if (
      commitPaste(entry.assignment, rebound, entry.assignment.crew_member_id, startTime, entry.mode)
    ) {
      clearClipboardAfterCut(entry.mode);
    }
  };

  // Paste the clipboard block into a stage's duties strip: re-bind it to that
  // stage as a stage-bound custom, keeping the source crew member.
  const pasteOntoStage = (stage: BriefingStageRecord, startTime: string) => {
    suppressNextClick();
    const entry = clipboard.entry;
    if (!entry) return;
    const rebound = rebindToStage(entry.assignment, { id: stage.id, label: stage.label });
    if (
      commitPaste(entry.assignment, rebound, entry.assignment.crew_member_id, startTime, entry.mode)
    ) {
      clearClipboardAfterCut(entry.mode);
    }
  };

  const pasteAtAnchor = (anchor: PasteAnchor) => {
    if (anchor.kind === "crew") pasteOntoCrew(anchor.crewMemberId, anchor.startTime);
    else if (anchor.kind === "stage") pasteOntoStage(anchor.stage, anchor.startTime);
    else pasteOntoArtist(anchor.artist, anchor.startTime);
  };

  // Paste at wherever the cursor/right-click last set the anchor. Shared by the
  // "Paste here" menu items and the Ctrl/Cmd+V shortcut.
  const pasteAtCurrentAnchor = () => {
    if (pasteAnchorRef.current) pasteAtAnchor(pasteAnchorRef.current);
  };

  // Keep keyboard handlers reading the latest closures without re-subscribing.
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    if (detailDialogOpen || customDialogOpen || aiDialogOpen || aiRegenerateDialogOpen) return;

    const command = matchScheduleShortcut(e);
    if (!command) return;

    if (command === "undo") {
      e.preventDefault();
      handleUndo();
    } else if (command === "redo") {
      e.preventDefault();
      handleRedo();
    } else if (command === "copy" && hoveredAssignmentRef.current) {
      e.preventDefault();
      handleCopyAssignment(hoveredAssignmentRef.current);
    } else if (command === "cut" && hoveredAssignmentRef.current) {
      e.preventDefault();
      handleCutAssignment(hoveredAssignmentRef.current);
    } else if (command === "paste" && clipboard.hasClipboard && pasteAnchorRef.current) {
      e.preventDefault();
      pasteAtCurrentAnchor();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div data-guide="step-5-tip-2">
            <Label>Select day</Label>
            <Select value={selectedDate} onValueChange={setSelectedDate}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {festivalDays.map((day) => (
                  <SelectItem key={day.date} value={day.date}>
                    {format(new Date(day.date), "MMMM d, yyyy")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Show assigned</Label>
            <Select value={selectedCrewFilter} onValueChange={setSelectedCrewFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {crewForDate
                  .filter((member) => member.id)
                  .map((member) => (
                    <SelectItem key={member.id} value={member.id!}>
                      {member.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.25))}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleUndo}
              disabled={!canUndo}
              title="Undo (Ctrl/Cmd+Z)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 7v6h6" />
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRedo}
              disabled={!canRedo}
              title="Redo (Ctrl/Cmd+Shift+Z)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 7v6h-6" />
                <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setClearAllConfirmOpen(true)}
              disabled={assignments.filter((a) => a.event_date === selectedDate).length === 0}
              title="Clear all assignments for this day"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowHighlights((v) => !v)}
              title={showHighlights ? "Hide artist highlights" : "Show artist highlights"}
              aria-pressed={showHighlights}
            >
              <Highlighter
                className={`h-4 w-4 ${showHighlights ? "text-primary" : "text-muted-foreground"}`}
              />
            </Button>

            {/* Schedule history: restore an earlier saved version. Reloads on
                restore so the editor renders the restored data cleanly. */}
            <ScheduleHistoryButton
              briefingId={briefingId}
              onBeforeRestore={captureDraftBeforeRestore}
              onRestored={() => window.location.reload()}
            />

            {/* Subtle autosave status. Manual saves publish to the crew;
                autosaves are silent recoverable drafts (see History). */}
            {autosave.status !== "idle" && (
              <span
                className="ml-1 text-xs text-muted-foreground"
                title="Autosaves are recoverable drafts and are not sent to your crew. Use Save to publish."
              >
                {autosave.status === "saving"
                  ? "Autosaving…"
                  : autosave.status === "saved"
                    ? "Autosaved draft"
                    : "Autosave failed"}
              </span>
            )}

            {/* Clear all confirmation dialog */}
            <Dialog open={clearAllConfirmOpen} onOpenChange={setClearAllConfirmOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Clear all assignments?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This will remove all{" "}
                  {assignments.filter((a) => a.event_date === selectedDate).length} assignment(s)
                  for this day. This action can be undone with the undo button.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setClearAllConfirmOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      const todayAssignments = assignments.filter(
                        (a) => a.event_date === selectedDate,
                      );
                      const filtered = assignments.filter((a) => a.event_date !== selectedDate);
                      saveToHistory(filtered);
                      toast.success(
                        `Cleared ${todayAssignments.length} assignment(s) for this day`,
                      );
                      setClearAllConfirmOpen(false);
                    }}
                  >
                    Clear all
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex gap-2">
          {/* Share with client from the creator → client sees it as Unpublished. */}
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShareOpen(true)}
            disabled={!briefingId}
          >
            <Share2 className="h-4 w-4" />
            Share with client
          </Button>
          {!hasAssignmentsForSelectedDate ? (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setAiDialogOpen(true)}
              data-guide="step-5-tip-1"
            >
              <Sparkles className="h-4 w-4" />
              Generate with AI
            </Button>
          ) : (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setAiRegenerateDialogOpen(true)}
              data-guide="step-5-tip-1"
            >
              <Sparkles className="h-4 w-4" />
              Re-generate with AI
            </Button>
          )}
          <Button
            onClick={async () => {
              setIsSaving(true);
              try {
                if (onSave) {
                  await onSave();
                } else {
                  toast.error("Save function not available");
                }
              } catch (error) {
                console.error(error);
                toast.error("Failed to save schedule");
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Card className="mb-4 p-4" data-guide="step-5-tip-3">
        <h3 className="mb-4 text-lg font-semibold">Schedules</h3>

        <StageFilterBar
          stages={stagesForDate}
          selected={selectedStages}
          onToggle={(label) => setSelectedStages((prev) => toggleStage(prev, label))}
          onClear={() => setSelectedStages(new Set())}
        />

        <TooltipProvider>
          <ResizablePanelSection panel={timelinePanel} label="schedule window">
            {/* select-none on the two drag-origin panels (here and the crew
                list): working fast means holding the left button while moving
                from a block to the crew list, which the browser reads as a
                text selection and lights the whole screen up. A selection
                only starts where the pointer went down, so suppressing it at
                the origin is enough — the header, day tabs and client
                feedback stay copyable. */}
            <ScrollArea
              className="w-full select-none"
              style={{ height: `${timelinePanel.height}px` }}
              ref={timelineRef}
              // Which drag the editor thinks is in flight. Every drop zone and
              // every "not dragging" click guard keys off this state, so it is
              // also the single thing an E2E test needs to assert that a drag
              // was let go of properly.
              data-drag-state={
                draggedAssignment || isDraggingBlock ? "assignment" : draggedCrew ? "crew" : "idle"
              }
            >
              <div className="relative pb-4" style={{ width: `${timelineWidth}px` }}>
                {/* Time Header */}
                <div className="sticky left-0 top-0 z-40 mb-4 border-b-2 border-border bg-card pb-2">
                  <div className="flex" style={{ minWidth: `${timelineWidth}px` }}>
                    <div
                      className="sticky left-0 z-40 shrink-0 bg-card"
                      style={{ width: `${labelWidth}px` }}
                    ></div>
                    <div
                      className="relative"
                      style={{ width: `${(endHour - startHour) * HOUR_WIDTH * zoomLevel}px` }}
                    >
                      <TimelineHourLabels
                        startHour={startHour}
                        hours={endHour - startHour}
                        pixelsPerHour={HOUR_WIDTH * zoomLevel}
                        formatTime={formatTime}
                        resolveScroller={resolveScrollViewport}
                        className="absolute -translate-x-1/2 text-sm font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* Current time marker label - above header */}
                {currentTimeMarker.visible && (
                  <CurrentTimeMarkerPill
                    offset={currentTimeMarker.position}
                    gridLeft={labelWidth}
                    label={formatTime(currentTimeMarker.time)}
                    zClass={CREATOR_LAYERS.currentTimeLabel.className}
                    resolveScroller={resolveScrollViewport}
                  />
                )}

                {/* Stage Rows */}
                {stagesToDisplay.map((stage) => {
                  // Scope to the selected day: briefingStages holds every day's
                  // rows, and labels repeat across days. Matching by label alone
                  // could pick another day's stage row, whose id isn't in the
                  // drag's orderedIds (stagesForSelectedDate) — reorderIds then
                  // returns the list unchanged and the drop "snaps back". The
                  // read-only timeline already uses the day-scoped stage id.
                  const stageRec = briefingStages.find(
                    (s) => s.event_date === selectedDate && s.label === (stage.label || stage.name),
                  );
                  const stageArtists = artistsForDate.filter((a) => {
                    if (stageRec && a.stage_id) return a.stage_id === stageRec.id;
                    return a.stage_name === (stage.label || stage.name);
                  });
                  // Custom assignments planned against this stage with no DJ
                  // (e.g. overview / audience). Rendered in the stage row so
                  // everything for the stage stays in one place.
                  const stageCustomAssignments = stageRec
                    ? assignmentsWithMirrors.filter(
                        (a) =>
                          a.event_date === selectedDate &&
                          isCustomForStage(a, stageRec.id) &&
                          isCrewMemberVisible(a.crew_member_id, {
                            hiddenCrew,
                            selectedCrewFilter,
                          }),
                      )
                    : [];

                  const rowKey = stageRowKey(stage);
                  const rowHeight = rowHeights.getRowHeight(rowKey, DEFAULT_STAGE_ROW_HEIGHT);

                  // Two bands per row: artist sets + their crew on top, a
                  // separate stage-duties strip at the bottom for stage-bound
                  // customs ("overzicht stage", "publiek", …) so those can't be
                  // mistaken for artist assignments. Lanes are computed per
                  // band, on the stored times (not the live-resize preview) so
                  // blocks don't reshuffle mid-drag.
                  const artistAssignmentsByArtist = new Map(
                    stageArtists.map((artist) => [
                      artist,
                      getArtistAssignments(artist).filter((assignment) =>
                        // Respect both the eye toggle (hiddenCrew) and the
                        // "Show assigned" dropdown — same rule as custom rows.
                        isCrewMemberVisible(assignment.crew_member_id, {
                          hiddenCrew,
                          selectedCrewFilter,
                        }),
                      ),
                    ]),
                  );
                  const laneRange = (a: Assignment) => ({
                    start: timeToPosition(a.filming_start),
                    end: timeToPosition(a.filming_end),
                  });
                  const artistLanes = assignRowLanes(
                    // Dedupe: a legacy assignment without lineup_id can
                    // name-match two lineup slots and would otherwise claim
                    // two lanes for one rendered block.
                    [
                      ...new Set(
                        stageArtists.flatMap(
                          (artist) => artistAssignmentsByArtist.get(artist) ?? [],
                        ),
                      ),
                    ],
                    laneRange,
                  );
                  const stageLanes = assignRowLanes(stageCustomAssignments, laneRange);

                  // Inner block area: top-1 (4px) + equal bottom gap. Artists
                  // keep this full area; the stage-duties strip is APPENDED
                  // below it, so the row grows instead of the artist blocks
                  // shrinking. During an assignment drag every stage row shows
                  // at least one strip lane as a drop target (drop there to
                  // turn the assignment into a stage duty). On stages without
                  // artists the duties normally fill the area itself.
                  const rowArea = { top: 4, height: rowHeight - ROW_BLOCK_PADDING };
                  const dutyLanes = maxLaneCount(stageLanes);
                  // Height is data-only: an empty lane is reserved up front so
                  // starting a drag (or filling the clipboard) can never
                  // resize the row under the producer's pointer. See
                  // stageDutyStripLanes for why that mattered.
                  const stripLanes = stageDutyStripLanes({
                    hasStageRow: !!stageRec,
                    hasArtists: stageArtists.length > 0,
                    dutyLanes,
                    reserveDropLane: true,
                  });
                  const stageStrip = stageDutyStrip(rowArea, stripLanes);
                  const dutyDropTop = stageDutyDropTop(stageStrip, {
                    hasStageRow: !!stageRec,
                    hasArtists: stageArtists.length > 0,
                  });
                  // The band the duties zone occupies: the appended strip, or
                  // the row area itself on a stage row without artists (which
                  // has no strip). Null = this row takes no duties at all.
                  const dutyZone = dutyDropTop === null ? null : (stageStrip.band ?? rowArea);
                  const totalRowHeight = rowHeight + stageStrip.extraHeight;
                  // Artist set-background block fills the full inner area.
                  const artistBlockHeight = rowArea.height;
                  // Inner band the assignment blocks occupy, inset inside the
                  // artist set-background block's 2px border so blocks sit flush
                  // within the frame: Tailwind top-1 (4px) + 2px border → starts
                  // at 6px, 4px (2×2px border) shorter than the background.
                  const rowBand = {
                    innerTop: 6,
                    innerHeight: Math.max(0, artistBlockHeight - 4),
                  };

                  return (
                    <div
                      key={stage.name}
                      className="mb-2"
                      data-testid="stage-row"
                      data-stage={stage.label || stage.name}
                    >
                      <div
                        className="flex items-stretch"
                        style={{
                          minWidth: `${timelineWidth}px`,
                        }}
                      >
                        <div
                          {...(stageRec ? stageReorder.getStageProps(stageRec.id) : {})}
                          style={{ width: `${labelWidth}px` }}
                          className={`sticky left-0 z-30 flex shrink-0 items-center gap-1 border-r-2 border-border bg-card px-2 text-sm font-medium ${LABEL_SEAM_COVER} ${ROW_SEAM.creator.coverClass} after:content-[''] ${
                            stageRec ? "cursor-grab active:cursor-grabbing" : ""
                          } ${
                            stageRec && stageReorder.overId === stageRec.id
                              ? "ring-2 ring-inset ring-primary"
                              : ""
                          } ${
                            stageRec && stageReorder.draggingId === stageRec.id ? "opacity-50" : ""
                          }`}
                          title={stageRec ? "Drag to reorder stages" : undefined}
                        >
                          {stageRec && (
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate">{stage.label || stage.name}</span>
                          <ScheduleRowResizeHandles
                            rowKey={rowKey}
                            rowHeight={rowHeight}
                            rowHeights={rowHeights}
                            labelWidth={labelWidthCtl}
                          />
                        </div>
                        <div
                          className="relative"
                          style={{
                            height: `${totalRowHeight}px`,
                            width: `${(endHour - startHour) * HOUR_WIDTH * zoomLevel}px`,
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => {
                            const dragged = activeDraggedAssignment();
                            if (dragged) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const clickX = e.clientX - rect.left;
                              const dropY = e.clientY - rect.top;

                              // Drop in the stage-duties strip.
                              if (dutyDropTop !== null && stageRec && dropY >= dutyDropTop) {
                                // An existing custom (stage duty or personal
                                // custom) is simply MOVED / re-bound to this
                                // stage — no dialog. Only artist-bound
                                // assignments go through the fill-in-subject
                                // conversion flow below.
                                if (dragged.assignment_type === "custom") {
                                  handleAssignmentDrop(e, "stage", undefined, undefined, stageRec);
                                  return;
                                }
                                // Artist-bound: nothing moves yet — prefill the
                                // custom dialog at the drop position and only
                                // convert to a stage duty when the producer
                                // saves. Closing the dialog cancels the move
                                // entirely.
                                e.preventDefault();
                                e.stopPropagation();
                                const startTime = viewportClientXToTime(e.clientX);
                                const duration = clockDurationMinutes(
                                  dragged.filming_start,
                                  dragged.filming_end,
                                  startHour,
                                );
                                setCustomSubject(dragged.subject || "");
                                setCustomLocation(dragged.location || "");
                                setCustomStageId(stageRec.id);
                                setCustomNotes(dragged.notes || "");
                                setCustomStart(startTime);
                                setCustomEnd(shiftTime(startTime, duration));
                                setSelectedCrewForCustom(dragged.crew_member_id);
                                setSelectedAssignmentForEdit(dragged);
                                setCustomDialogOpen(true);
                                clearAssignmentDrag();
                                return;
                              }

                              // Find which artist the drop is on based on time.
                              // Snap to the set edges (not just the 15-min grid) so a
                              // drop on an off-grid start (e.g. 18:20) lands on that
                              // set and binds to the artist, instead of grid-snapping
                              // to 18:15 and falling outside it.
                              const dropTime = positionToSnappedTimeMulti(
                                clickX,
                                startHour,
                                zoomLevel,
                                stageArtists.flatMap((a) => [a.set_start, a.set_end]),
                                "floor",
                              );

                              // Find artist that contains this time
                              const targetArtist = artistAtTime(stageArtists, dropTime);

                              if (
                                !targetArtist &&
                                dragged.assignment_type === "custom" &&
                                stageRec
                              ) {
                                // Move a stage-bound custom to THIS stage.
                                handleAssignmentDrop(e, "stage", undefined, undefined, stageRec);
                              } else if (!targetArtist) {
                                // An artist-bound block dropped in a gap has
                                // nothing to bind to: handleAssignmentDrop would
                                // move its TIMES while leaving it attached to the
                                // artist it came from, so it would snap back to
                                // its old row — the same "the move did nothing"
                                // confusion this whole flow exists to avoid. Say
                                // so instead. Drop it in the duties strip to turn
                                // it into a stage duty.
                                toast.error("No artist plays here — drop it on a set or a duty");
                                clearAssignmentDrag();
                              } else {
                                handleAssignmentDrop(e, "artist", targetArtist);
                              }
                            } else if (draggedCrew) {
                              // Drop a crew member straight onto the stage —
                              // works even when the stage has no DJs yet.
                              handleDropCrewOnStage(stageRec, stageArtists, e);
                            }
                          }}
                        >
                          {/* Client feedback markers for this stage */}
                          {renderRowFeedback(stage.label || stage.name)}

                          {/* Current time marker segment */}
                          {currentTimeMarker.visible && (
                            <CurrentTimeMarkerLine
                              left={currentTimeMarker.position}
                              zClass={CREATOR_LAYERS.currentTimeLine.className}
                              bridge={ROW_SEAM.creator.bridgePx}
                            />
                          )}

                          {/* Time grid lines */}
                          {Array.from({ length: (endHour - startHour) * 4 }, (_, i) => {
                            const isFullHour = i % 4 === 0;
                            const isDropZone =
                              resizingAssignment || draggedAssignment || draggedCrew;
                            return (
                              <div
                                key={i}
                                className={`absolute bottom-0 top-0 transition-all ${
                                  isDropZone
                                    ? "border-l-2 border-primary/40"
                                    : isFullHour
                                      ? "border-l border-border"
                                      : "border-l border-border/30"
                                }`}
                                style={{ left: `${i * 60 * zoomLevel}px` }}
                              />
                            );
                          })}

                          {/* Artist blocks */}
                          {stageArtists.map((artist, idx) => {
                            const left = timeToPosition(artist.set_start);
                            const width = getDuration(artist.set_start, artist.set_end);
                            const isActivated = isArtistActivated(artist);
                            // Highlighted artists keep the brand-green glow (shared
                            // Base set background. The green highlight ring/glow is
                            // NOT painted here — it's a separate top-layer overlay
                            // below (after the assignments) so the glow always sits
                            // above the crew-assignment blocks and frames them,
                            // rather than being painted over by the z-10 assignments.
                            // Highlight visuals (green ring + border) only when the
                            // artist is marked highlighted AND the producer hasn't
                            // toggled highlights off via the toolbar.
                            const showGlow = artistGlowVisible(artist.highlighted, showHighlights);
                            const artistBlockClass = [
                              // transition-colors, NOT transition-all: the height follows the
                              // row-height drag handle and must track it instantly instead
                              // of animating behind it.
                              "absolute top-1 cursor-pointer rounded border-2 bg-muted/30 transition-colors",
                              !isActivated && "opacity-30",
                              showGlow || draggedCrew ? "border-primary" : "border-border",
                              draggedCrew ? "border-dashed" : "hover:bg-muted/50",
                            ]
                              .filter(Boolean)
                              .join(" ");

                            return (
                              <div key={idx}>
                                {/* Artist set background */}
                                <Tooltip open={resizingAssignment ? false : undefined}>
                                  <ContextMenu>
                                    <ContextMenuTrigger asChild>
                                      <TooltipTrigger asChild>
                                        <div
                                          className={artistBlockClass}
                                          style={{
                                            left: `${left}px`,
                                            width: `${width}px`,
                                            height: `${artistBlockHeight}px`,
                                          }}
                                          onClick={(e) => {
                                            if (isStrayMenuClick(e)) return;
                                            if (
                                              !draggedCrew &&
                                              !draggedAssignment &&
                                              !justFinishedResizing
                                            )
                                              // Start the new block where the producer
                                              // clicked, not at the set start.
                                              handleArtistClick(
                                                artist,
                                                undefined,
                                                viewportClientXToTime(e.clientX),
                                              );
                                          }}
                                          onContextMenu={(e) => {
                                            suppressNextClick();
                                            setArtistAnchor(e, artist);
                                          }}
                                          onMouseMove={(e) => setArtistAnchor(e, artist)}
                                          onDrop={(e) => {
                                            if (draggedCrew) {
                                              handleDropOnArtist(artist, e);
                                            } else if (activeDraggedAssignment()) {
                                              handleAssignmentDrop(e, "artist", artist);
                                            }
                                          }}
                                          onDragOver={handleDragOver}
                                        >
                                          {/* 15-minute segments when dragging crew */}
                                          {draggedCrew &&
                                            Array.from(
                                              {
                                                length: Math.floor(
                                                  getDuration(artist.set_start, artist.set_end) /
                                                    (60 * zoomLevel),
                                                ),
                                              },
                                              (_, i) => (
                                                <div
                                                  key={i}
                                                  className="absolute bottom-0 top-0 border-l-2 border-primary/40"
                                                  style={{ left: `${i * 60 * zoomLevel}px` }}
                                                />
                                              ),
                                            )}
                                          <div className="truncate p-2 text-xs font-medium">
                                            {artist.artist_name}
                                            <span className="ml-1 opacity-60">
                                              {formatTime(artist.set_start)} -{" "}
                                              {formatTime(artist.set_end)}
                                            </span>
                                          </div>
                                        </div>
                                      </TooltipTrigger>
                                    </ContextMenuTrigger>
                                    <ContextMenuContent>
                                      <ContextMenuItem
                                        className={ITEM_FOCUS}
                                        disabled={!clipboard.hasClipboard}
                                        onSelect={pasteAtCurrentAnchor}
                                      >
                                        <ClipboardPaste className="mr-2 h-4 w-4" />
                                        Paste here
                                      </ContextMenuItem>
                                    </ContextMenuContent>
                                  </ContextMenu>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <div className="font-semibold">{artist.artist_name}</div>
                                      <div>{stage.label || stage.name}</div>
                                      <div>
                                        {formatTime(artist.set_start)} -{" "}
                                        {formatTime(artist.set_end)}
                                      </div>
                                      {draggedCrew && (
                                        <div className="mt-1 font-semibold text-primary">
                                          Drop to assign
                                        </div>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>

                                {/* Filming assignments (visibility already
                                  applied in artistAssignmentsByArtist) */}
                                {(artistAssignmentsByArtist.get(artist) ?? []).map(
                                  (assignment, aIdx) => {
                                    const crew = getCrewMember(assignment.crew_member_id);
                                    const liveTimes = liveTimesFor(assignment);
                                    const filmLeft = timeToPosition(liveTimes.filming_start);
                                    const filmWidth = getDuration(
                                      liveTimes.filming_start,
                                      liveTimes.filming_end,
                                    );

                                    // Lane within the artist band (splits with
                                    // the other artist-bound blocks in the row).
                                    const lane = artistLanes.get(assignment) ?? {
                                      laneIndex: 0,
                                      laneCount: 1,
                                    };
                                    const { top: topOffset, height: blockHeight } =
                                      stackedBlockLayout({
                                        ...rowBand,
                                        totalOverlapping: lane.laneCount,
                                        overlapIndex: lane.laneIndex,
                                      });

                                    // Assistant mirror PREVIEW: read-only, dashed,
                                    // non-interactive (pointer-events-none so it
                                    // never blocks drops on the artist underneath).
                                    if (assignment.__mirror) {
                                      return (
                                        <div
                                          key={aIdx}
                                          className="pointer-events-none absolute z-10 rounded border-2 border-dashed border-white/80 opacity-80"
                                          style={{
                                            left: `${filmLeft}px`,
                                            width: `${filmWidth}px`,
                                            top: `${topOffset}px`,
                                            height: `${blockHeight}px`,
                                            backgroundColor: crew?.color || "#666",
                                          }}
                                        >
                                          <div className="flex h-full flex-col justify-end p-2 text-xs font-medium text-white">
                                            <div className="flex items-center gap-1 truncate opacity-90">
                                              <UserRoundPlus className="h-3 w-3 shrink-0" />
                                              <span className="truncate">{crew?.name}</span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <AssignmentContextMenu
                                        key={aIdx}
                                        tooltipOpen={resizingAssignment ? false : undefined}
                                        canPaste={clipboard.hasClipboard}
                                        onCopy={() => handleCopyAssignment(assignment)}
                                        onCut={() => handleCutAssignment(assignment)}
                                        onDuplicate={() => handleDuplicateAssignment(assignment)}
                                        onPaste={() =>
                                          pasteOntoArtist(
                                            artist,
                                            normalizeTime(assignment.filming_end),
                                          )
                                        }
                                        assistExcludable={canExcludeAssist(
                                          assignment.crew_member_id,
                                        )}
                                        assistExcluded={!!assignment.mirror_excluded}
                                        onToggleAssistExclusion={() =>
                                          handleToggleMirrorExcluded(assignment)
                                        }
                                        onDelete={() => handleDeleteAssignment(assignment)}
                                        tooltip={
                                          <div className="text-xs">
                                            <div className="font-semibold">
                                              {artist.artist_name}
                                            </div>
                                            <div>{crew?.name}</div>
                                            <div>
                                              {formatTime(assignment.filming_start)} -{" "}
                                              {formatTime(assignment.filming_end)}
                                            </div>
                                            {assignment.notes && (
                                              <div className="mt-1 text-muted-foreground">
                                                {assignment.notes}
                                              </div>
                                            )}
                                            {!isActivated && (
                                              <div className="mt-1 font-semibold text-yellow-500">
                                                ⚠ Artist deactivated
                                              </div>
                                            )}
                                            <div className="mt-1 font-semibold text-primary">
                                              Drag to reposition | Resize edges
                                            </div>
                                          </div>
                                        }
                                      >
                                        <div
                                          data-testid="assignment-block"
                                          className={`group absolute z-10 cursor-move rounded transition-opacity ${
                                            isActivated
                                              ? "opacity-80 hover:opacity-100"
                                              : "opacity-40 hover:opacity-60"
                                          }`}
                                          style={{
                                            left: `${filmLeft}px`,
                                            width: `${filmWidth}px`,
                                            top: `${topOffset}px`,
                                            height: `${blockHeight}px`,
                                            backgroundColor: crew?.color || "#666",
                                            ...leavingStyle(assignment),
                                          }}
                                          draggable
                                          onDragStart={(e) =>
                                            handleAssignmentDragStart(e, assignment)
                                          }
                                          onDragEnd={handleAssignmentDragEnd}
                                          onDragOver={handleDragOver}
                                          onDrop={(e) => {
                                            if (draggedCrew) {
                                              handleDropOnArtist(artist, e);
                                            } else if (activeDraggedAssignment()) {
                                              handleAssignmentDrop(e, "artist", artist);
                                            }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isStrayMenuClick(e)) return;
                                            if (!justFinishedResizing) {
                                              handleArtistClick(artist, assignment);
                                            }
                                          }}
                                          onContextMenu={() => {
                                            suppressNextClick();
                                          }}
                                          onMouseEnter={() =>
                                            (hoveredAssignmentRef.current = assignment)
                                          }
                                          onMouseMove={(e) => setArtistAnchor(e, artist)}
                                          onMouseLeave={() => {
                                            if (hoveredAssignmentRef.current === assignment)
                                              hoveredAssignmentRef.current = null;
                                          }}
                                        >
                                          {/* Resize handle - left edge */}
                                          <div
                                            className="absolute bottom-0 left-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                            onMouseDown={(e) =>
                                              handleResizeStart(e, assignment, "start")
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          />

                                          {/* When two or more crew share a slot the block is
                                            halved vertically; dropping the artist + time
                                            lines keeps the crew name (the only line that
                                            differs between stacked blocks) readable. Both
                                            stay accessible via the tooltip. */}
                                          <div className="flex h-full flex-col justify-end overflow-hidden p-2 text-xs font-medium text-white">
                                            {lane.laneCount <= 1 && (
                                              <div className="truncate text-[10px] uppercase tracking-wider opacity-80">
                                                {artist.artist_name}
                                              </div>
                                            )}
                                            {lane.laneCount <= 1 && (
                                              <div className="truncate opacity-70">
                                                {formatTime(liveTimes.filming_start)} -{" "}
                                                {formatTime(liveTimes.filming_end)}
                                              </div>
                                            )}
                                            <div className="truncate opacity-90">{crew?.name}</div>
                                          </div>

                                          {/* Resize handle - right edge */}
                                          <div
                                            className="absolute bottom-0 right-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                            onMouseDown={(e) =>
                                              handleResizeStart(e, assignment, "end")
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                      </AssignmentContextMenu>
                                    );
                                  },
                                )}

                                {/* Highlight ring/glow — sits above the z-10 crew
                                  assignments so it frames them, but BELOW the
                                  z-30 sticky stage-label column (z-20) so the
                                  glow scrolls neatly behind the stage names
                                  instead of bleeding over them. pointer-events-none
                                  keeps it purely visual so clicks/drag-drop pass
                                  through to the block and assignments underneath.
                                  Dashed during a crew drag to match the
                                  drop-target affordance. Hidden when the producer
                                  toggles highlights off. */}
                                {showGlow && (
                                  <div
                                    aria-hidden
                                    className={`glow-highlight pointer-events-none absolute top-1 z-20 rounded border-2 border-primary ${
                                      draggedCrew ? "border-dashed" : ""
                                    }`}
                                    style={{
                                      left: `${left}px`,
                                      width: `${width}px`,
                                      height: `${artistBlockHeight}px`,
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}

                          {/* Stage-level custom assignments (crew filming this
                            stage with no DJ — e.g. overview / audience). Same
                            block as the per-crew Custom row, rendered here so
                            the stage stays the single home for its planning. */}
                          {stageCustomAssignments.map((assignment, idx) => {
                            const crew = getCrewMember(assignment.crew_member_id);
                            const liveTimes = liveTimesFor(assignment);
                            const left = timeToPosition(liveTimes.filming_start);
                            const width = getDuration(
                              liveTimes.filming_start,
                              liveTimes.filming_end,
                            );

                            // Fixed-height lane in the appended strip; on stages
                            // without artists the duties fill the row area
                            // itself (unless a drag temporarily shows the strip).
                            const lane = stageLanes.get(assignment) ?? {
                              laneIndex: 0,
                              laneCount: 1,
                            };
                            const { top: topOffset, height: blockHeight } = dutyBlockLayout(
                              stageStrip,
                              rowArea,
                              lane,
                            );
                            const dutyColor = crew?.color || "#666";

                            if (assignment.__mirror) {
                              return (
                                <div
                                  key={`stage-custom-${idx}`}
                                  className="pointer-events-none absolute rounded border-2 border-dashed border-white/80 opacity-80"
                                  style={{
                                    left: `${left}px`,
                                    width: `${width}px`,
                                    top: `${topOffset}px`,
                                    height: `${blockHeight}px`,
                                    backgroundColor: dutyColor,
                                  }}
                                >
                                  <div className="flex h-full items-center overflow-hidden px-1.5 text-[10px] font-medium text-white">
                                    <UserRoundPlus className="mr-1 h-3 w-3 shrink-0" />
                                    <span className="truncate uppercase tracking-wider">
                                      {assignment.subject}
                                    </span>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <AssignmentContextMenu
                                key={`stage-custom-${idx}`}
                                tooltipOpen={resizingAssignment ? false : undefined}
                                canPaste={clipboard.hasClipboard}
                                onCopy={() => handleCopyAssignment(assignment)}
                                onCut={() => handleCutAssignment(assignment)}
                                onDuplicate={() => handleDuplicateAssignment(assignment)}
                                onPaste={() =>
                                  pasteOntoCrew(
                                    assignment.crew_member_id,
                                    normalizeTime(assignment.filming_end),
                                  )
                                }
                                assistExcludable={canExcludeAssist(assignment.crew_member_id)}
                                assistExcluded={!!assignment.mirror_excluded}
                                onToggleAssistExclusion={() =>
                                  handleToggleMirrorExcluded(assignment)
                                }
                                onDelete={() => handleDeleteAssignment(assignment)}
                                tooltip={
                                  <div className="text-xs">
                                    <div className="font-semibold">{assignment.subject}</div>
                                    <div>{stage.label || stage.name}</div>
                                    {assignment.location &&
                                      assignment.location.toLowerCase() !==
                                        (assignment.subject ?? "").toLowerCase() &&
                                      assignment.location.toLowerCase() !==
                                        (stage.label || stage.name).toLowerCase() && (
                                        <div className="text-muted-foreground">
                                          {assignment.location}
                                        </div>
                                      )}
                                    <div>{crew?.name}</div>
                                    <div>
                                      {formatTime(assignment.filming_start)} -{" "}
                                      {formatTime(assignment.filming_end)}
                                    </div>
                                    {assignment.notes && (
                                      <div className="mt-1 text-muted-foreground">
                                        {assignment.notes}
                                      </div>
                                    )}
                                    <div className="mt-1 font-semibold text-primary">
                                      Drag to reposition | Resize edges
                                    </div>
                                  </div>
                                }
                              >
                                {/* Outlined + translucent, visually distinct
                                  from the solid artist-bound blocks above. */}
                                <div
                                  className="group absolute z-10 rounded border-2 opacity-90 transition-opacity hover:opacity-100"
                                  style={{
                                    left: `${left}px`,
                                    width: `${width}px`,
                                    top: `${topOffset}px`,
                                    height: `${blockHeight}px`,
                                    borderColor: dutyColor,
                                    backgroundColor: dutyColor,
                                    ...leavingStyle(assignment),
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isStrayMenuClick(e)) return;
                                    if (!justFinishedResizing) {
                                      setCustomSubject(assignment.subject || "");
                                      setCustomLocation(assignment.location || "");
                                      setCustomStageId(assignment.stage_id ?? null);
                                      setCustomNotes(assignment.notes || "");
                                      setCustomStart(assignment.filming_start);
                                      setCustomEnd(assignment.filming_end);
                                      setSelectedCrewForCustom(assignment.crew_member_id);
                                      setSelectedAssignmentForEdit(assignment);
                                      setCustomDialogOpen(true);
                                    }
                                  }}
                                  onContextMenu={() => {
                                    suppressNextClick();
                                  }}
                                  onMouseEnter={() => (hoveredAssignmentRef.current = assignment)}
                                  onMouseLeave={() => {
                                    if (hoveredAssignmentRef.current === assignment)
                                      hoveredAssignmentRef.current = null;
                                  }}
                                >
                                  {/* Resize handle - left edge */}
                                  <div
                                    className="absolute bottom-0 left-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                    onMouseDown={(e) => handleResizeStart(e, assignment, "start")}
                                    onClick={(e) => e.stopPropagation()}
                                  />

                                  <div
                                    className="flex h-full cursor-move items-center overflow-hidden px-1.5 text-[10px] font-medium text-white"
                                    draggable
                                    onDragStart={(e) => handleAssignmentDragStart(e, assignment)}
                                    onDragEnd={handleAssignmentDragEnd}
                                  >
                                    <span className="truncate">
                                      {assignment.subject && (
                                        <span className="uppercase tracking-wider opacity-90">
                                          {assignment.subject} ·{" "}
                                        </span>
                                      )}
                                      {crew?.name}
                                    </span>
                                  </div>

                                  {/* Resize handle - right edge */}
                                  <div
                                    className="absolute bottom-0 right-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                    onMouseDown={(e) => handleResizeStart(e, assignment, "end")}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </AssignmentContextMenu>
                            );
                          })}

                          {/* Stage-duties zone: invisible right-click paste
                            target ("Paste here" as stage duty); while dragging
                            an assignment it doubles as the dashed drop hint.
                            Normally the appended strip — but a stage row
                            WITHOUT artists has no strip, because its duties
                            fill the row area, so there the whole area is the
                            zone (matching stageDutyDropTop). Without that
                            fallback such a row would accept duty drops while
                            showing the producer no target at all. It stays
                            behind the duty blocks there (z-0) so it frames the
                            row instead of covering what's in it. */}
                          {dutyZone && stageRec && (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <div
                                  className={
                                    draggedAssignment
                                      ? `absolute ${stageStrip.band ? "z-10" : "z-0"} flex items-center justify-center rounded border-2 border-dashed border-primary/50 text-[10px] font-medium uppercase tracking-wider text-primary/70`
                                      : "absolute z-0"
                                  }
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: `${dutyZone.top}px`,
                                    height: `${dutyZone.height}px`,
                                  }}
                                  onMouseMove={(e) => setStageAnchor(e, stageRec)}
                                  onContextMenu={(e) => setStageAnchor(e, stageRec)}
                                >
                                  {draggedAssignment ? "Stage duty" : null}
                                </div>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem
                                  className={ITEM_FOCUS}
                                  disabled={!clipboard.hasClipboard}
                                  onSelect={pasteAtCurrentAnchor}
                                >
                                  <ClipboardPaste className="mr-2 h-4 w-4" />
                                  Paste here
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Custom rows for filtered crew */}
                {filteredCrewForDate.map((crewMember) => {
                  // Include derived mirror custom blocks so an assistant's custom
                  // row shows the assisted member's custom subjects (read-only).
                  const customAssignments = assignmentsWithMirrors.filter(
                    (a) =>
                      a.crew_member_id === crewMember.id &&
                      a.assignment_type === "custom" &&
                      // Stage-bound customs live in their stage row instead.
                      !isStageBoundCustom(a, dayStageIdSet),
                  );

                  const rowKey = customRowKey(crewMember.id || crewMember.name);
                  const rowHeight = rowHeights.getRowHeight(rowKey, DEFAULT_CUSTOM_ROW_HEIGHT);
                  const customBlockHeight = rowHeight - ROW_BLOCK_PADDING;

                  return (
                    <div key={crewMember.id} className="mb-2">
                      <div
                        className="flex items-stretch"
                        style={{
                          minWidth: `${timelineWidth}px`,
                        }}
                      >
                        <div
                          style={{ width: `${labelWidth}px` }}
                          className={`sticky left-0 z-30 flex shrink-0 items-center border-r-2 border-border bg-card px-2 text-sm font-medium ${LABEL_SEAM_COVER} ${ROW_SEAM.creator.coverClass} after:content-['']`}
                        >
                          <span className="truncate">Custom - {crewMember.name}</span>
                          <ScheduleRowResizeHandles
                            rowKey={rowKey}
                            rowHeight={rowHeight}
                            rowHeights={rowHeights}
                            labelWidth={labelWidthCtl}
                          />
                        </div>
                        <ContextMenu>
                          <ContextMenuTrigger asChild>
                            <div
                              className={`relative cursor-pointer transition-colors ${
                                draggedCrew === crewMember.id
                                  ? "border-dashed border-primary bg-primary/10"
                                  : "hover:bg-muted/10"
                              }`}
                              style={{
                                height: `${rowHeight}px`,
                                width: `${(endHour - startHour) * HOUR_WIDTH * zoomLevel}px`,
                              }}
                              onClick={(e) =>
                                !draggedAssignment && handleTimelineClick(e, crewMember.id || "")
                              }
                              onDrop={(e) => {
                                if (draggedCrew === crewMember.id) {
                                  handleDropOnCustom(crewMember.id || "", e);
                                } else if (activeDraggedAssignment()) {
                                  handleAssignmentDrop(e, "custom", undefined, crewMember.id);
                                }
                              }}
                              onDragOver={handleDragOver}
                              onMouseMove={(e) => setCrewAnchor(e, crewMember.id || "")}
                              onContextMenu={(e) => setCrewAnchor(e, crewMember.id || "")}
                            >
                              {/* Client feedback markers for this custom row */}
                              {renderRowFeedback(`Custom - ${crewMember.name}`)}

                              {/* Current time marker segment */}
                              {currentTimeMarker.visible && (
                                <CurrentTimeMarkerLine
                                  left={currentTimeMarker.position}
                                  zClass={CREATOR_LAYERS.currentTimeLine.className}
                                  bridge={ROW_SEAM.creator.bridgePx}
                                />
                              )}

                              {/* Time grid lines */}
                              {Array.from({ length: (endHour - startHour) * 4 }, (_, i) => {
                                const isFullHour = i % 4 === 0;
                                const isDropZone = isDraggingBlock || draggedAssignment;
                                return (
                                  <div
                                    key={i}
                                    className={`absolute bottom-0 top-0 transition-all ${
                                      isDropZone
                                        ? "border-l-2 border-primary bg-primary/5"
                                        : isFullHour
                                          ? "border-l border-border"
                                          : "border-l border-border/30"
                                    }`}
                                    style={{ left: `${i * 60 * zoomLevel}px` }}
                                  />
                                );
                              })}

                              {/* Custom blocks */}
                              {customAssignments.map((assignment, idx) => {
                                const liveTimes = liveTimesFor(assignment);
                                const left = timeToPosition(liveTimes.filming_start);
                                const width = getDuration(
                                  liveTimes.filming_start,
                                  liveTimes.filming_end,
                                );

                                // Assistant mirror PREVIEW: read-only, dashed.
                                if (assignment.__mirror) {
                                  return (
                                    <div
                                      key={idx}
                                      className="pointer-events-none absolute top-1 rounded border-2 border-dashed border-white/80 opacity-80"
                                      style={{
                                        left: `${left}px`,
                                        width: `${width}px`,
                                        height: `${customBlockHeight}px`,
                                        backgroundColor: crewMember.color || "#666",
                                      }}
                                    >
                                      <div className="flex h-full flex-col justify-end overflow-hidden p-2 text-xs font-medium text-white">
                                        <div className="flex items-center gap-1 truncate uppercase tracking-wider">
                                          <UserRoundPlus className="h-3 w-3 shrink-0" />
                                          <span className="truncate">{assignment.subject}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <AssignmentContextMenu
                                    key={idx}
                                    tooltipOpen={resizingAssignment ? false : undefined}
                                    canPaste={clipboard.hasClipboard}
                                    onCopy={() => handleCopyAssignment(assignment)}
                                    onCut={() => handleCutAssignment(assignment)}
                                    onDuplicate={() => handleDuplicateAssignment(assignment)}
                                    onPaste={() =>
                                      pasteOntoCrew(
                                        assignment.crew_member_id,
                                        normalizeTime(assignment.filming_end),
                                      )
                                    }
                                    assistExcludable={canExcludeAssist(assignment.crew_member_id)}
                                    assistExcluded={!!assignment.mirror_excluded}
                                    onToggleAssistExclusion={() =>
                                      handleToggleMirrorExcluded(assignment)
                                    }
                                    onDelete={() => handleDeleteAssignment(assignment)}
                                    tooltip={
                                      <div className="text-xs">
                                        <div className="font-semibold">{assignment.subject}</div>
                                        {assignment.stage_name &&
                                          !assignment.stage_name.startsWith("Custom") &&
                                          assignment.stage_name.toLowerCase() !==
                                            (assignment.subject ?? "").toLowerCase() && (
                                            <div>{getStageLabel(assignment)}</div>
                                          )}
                                        {assignment.location &&
                                          assignment.location.toLowerCase() !==
                                            (assignment.subject ?? "").toLowerCase() &&
                                          assignment.location.toLowerCase() !==
                                            (assignment.stage_name ?? "").toLowerCase() && (
                                            <div className="text-muted-foreground">
                                              {assignment.location}
                                            </div>
                                          )}
                                        <div>{crewMember.name}</div>
                                        <div>
                                          {formatTime(assignment.filming_start)} -{" "}
                                          {formatTime(assignment.filming_end)}
                                        </div>
                                        {assignment.notes && (
                                          <div className="mt-1 text-muted-foreground">
                                            {assignment.notes}
                                          </div>
                                        )}
                                        <div className="mt-1 font-semibold text-primary">
                                          Drag to reposition | Resize edges
                                        </div>
                                      </div>
                                    }
                                  >
                                    <div
                                      className="group absolute top-1 rounded opacity-90 transition-opacity hover:opacity-100"
                                      style={{
                                        left: `${left}px`,
                                        width: `${width}px`,
                                        height: `${customBlockHeight}px`,
                                        backgroundColor: crewMember.color || "#666",
                                        ...leavingStyle(assignment),
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isStrayMenuClick(e)) return;
                                        if (!justFinishedResizing) {
                                          // Open edit dialog for custom assignment
                                          setCustomSubject(assignment.subject || "");
                                          setCustomLocation(assignment.location || "");
                                          setCustomStageId(assignment.stage_id ?? null);
                                          setCustomNotes(assignment.notes || "");
                                          setCustomStart(assignment.filming_start);
                                          setCustomEnd(assignment.filming_end);
                                          setSelectedCrewForCustom(crewMember.id || "");
                                          setSelectedAssignmentForEdit(assignment);
                                          setCustomDialogOpen(true);
                                        }
                                      }}
                                      onContextMenu={() => {
                                        suppressNextClick();
                                      }}
                                      onMouseEnter={() =>
                                        (hoveredAssignmentRef.current = assignment)
                                      }
                                      onMouseLeave={() => {
                                        if (hoveredAssignmentRef.current === assignment)
                                          hoveredAssignmentRef.current = null;
                                      }}
                                    >
                                      {/* Resize handle - left edge */}
                                      <div
                                        className="absolute bottom-0 left-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                        onMouseDown={(e) =>
                                          handleResizeStart(e, assignment, "start")
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />

                                      <div
                                        className="flex h-full cursor-move flex-col justify-end overflow-hidden p-2 text-xs font-medium text-white"
                                        draggable
                                        onDragStart={(e) =>
                                          handleAssignmentDragStart(e, assignment)
                                        }
                                        onDragEnd={handleAssignmentDragEnd}
                                      >
                                        {assignment.subject && (
                                          <div className="mb-0.5 truncate text-[10px] uppercase tracking-wider opacity-90">
                                            {assignment.subject.toLowerCase().includes("break")
                                              ? "Break"
                                              : assignment.subject}
                                          </div>
                                        )}
                                        <div className="truncate">
                                          {formatTime(liveTimes.filming_start)} -{" "}
                                          {formatTime(liveTimes.filming_end)}
                                        </div>
                                      </div>

                                      {/* Resize handle - right edge */}
                                      <div
                                        className="absolute bottom-0 right-0 top-0 z-10 w-2 cursor-ew-resize transition-colors hover:bg-white/30"
                                        onMouseDown={(e) => handleResizeStart(e, assignment, "end")}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  </AssignmentContextMenu>
                                );
                              })}
                            </div>
                          </ContextMenuTrigger>
                          <ContextMenuContent>
                            <ContextMenuItem
                              className={ITEM_FOCUS}
                              disabled={!clipboard.hasClipboard}
                              onSelect={pasteAtCurrentAnchor}
                            >
                              <ClipboardPaste className="mr-2 h-4 w-4" />
                              Paste here
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
              <ScrollBar
                orientation="horizontal"
                className="opacity-100"
                thumbClassName="bg-[#7b9834]"
              />
            </ScrollArea>
          </ResizablePanelSection>
        </TooltipProvider>
      </Card>

      {/* Crew Legend - full width, resizable. select-none: see the timeline. */}
      <Card className="select-none p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-semibold">Drag to assign</h4>
          <div className="flex items-center gap-2">
            <Label htmlFor="crew-list-sort" className="text-xs text-muted-foreground">
              Sort
            </Label>
            <Select
              value={crewListSort}
              onValueChange={(value) => setCrewListSort(value as CrewSortOrder)}
            >
              <SelectTrigger id="crew-list-sort" className="h-8 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CREW_SORT_LABELS).map(([order, label]) => (
                  <SelectItem key={order} value={order}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* No title: the card heading above already names the list. */}
        <FunctionFilterBar
          crew={activeCrewForDate}
          selected={crewListFunctions}
          onToggle={(fn) => setCrewListFunctions((prev) => toggleFunction(prev, fn))}
          onClear={() => setCrewListFunctions(new Set())}
        />
        <ResizablePanelSection panel={crewListPanel} label="crew list">
          <ScrollArea style={{ height: `${crewListPanel.height}px` }}>
            {/* Only when a filter is hiding everyone — an empty crew list on
                a day with nobody activated speaks for itself. */}
            {crewListForPicking.length === 0 && crewListFunctions.size > 0 && (
              <p className="py-3 text-sm text-muted-foreground">
                No crew matches the selected function filter.
              </p>
            )}
            {/* Full width since the Tips card was dropped, so wider screens
                fit a third and fourth column of crew without cramping the
                rows (the role label sits right-aligned in each cell). */}
            <div className="grid grid-cols-2 gap-2 xl:grid-cols-3 2xl:grid-cols-4">
              {crewListForPicking.map((member) => {
                const isHidden = hiddenCrew.has(member.id || "");
                // Assistant linking is driven from the filming member's side:
                // only ASSISTABLE_ROLES can be assisted, and only crew in
                // ASSISTANT_ROLES can be picked as assistants (see crewRoles).
                const memberAssistable = isAssistableRole(member.function);
                const memberIsAssistant = isAssistantRole(member.function);
                const assistCandidates = memberAssistable
                  ? assistantCrewForDate.filter((c) => c.id !== member.id)
                  : [];
                const assignedAssistants = memberAssistable
                  ? activeCrewForDate.filter((c) => c.id && assistLinks[c.id] === member.id)
                  : [];
                const assistingName = memberIsAssistant
                  ? activeCrewForDate.find((c) => c.id === assistLinks[member.id || ""])?.name
                  : undefined;
                // Mark assistants' colour swatch with a dashed ring so it's
                // clear those blocks are mirrored from someone else.
                const memberIsAssisting = !!assistLinks[member.id || ""];
                return (
                  <div
                    key={member.id}
                    className={`rounded p-2 transition-colors ${
                      isHidden
                        ? "opacity-40"
                        : draggedCrew === member.id
                          ? "border-2 border-primary bg-primary/10"
                          : "hover:bg-muted/50"
                    }`}
                  >
                    {/* Top row: drag handle + identity */}
                    <div
                      className={`flex items-center gap-2 ${!isHidden ? "cursor-move" : ""}`}
                      draggable={!isHidden}
                      onDragStart={(e) => !isHidden && handleCrewDragStart(e, member.id || "")}
                      onDragEnd={handleCrewDragEnd}
                    >
                      <button
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHiddenCrew((prev) => {
                            const next = new Set(prev);
                            if (next.has(member.id || "")) {
                              next.delete(member.id || "");
                            } else {
                              next.add(member.id || "");
                            }
                            return next;
                          });
                        }}
                      >
                        {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <div
                        className={`h-4 w-4 shrink-0 rounded ${
                          memberIsAssisting
                            ? "outline-dashed outline-2 outline-offset-2 outline-muted-foreground"
                            : ""
                        }`}
                        style={{ backgroundColor: member.color || "#666" }}
                        title={memberIsAssisting ? "Assistant" : undefined}
                      />
                      <span className="truncate text-sm">{member.name}</span>
                      <span className="ml-auto truncate text-xs text-muted-foreground">
                        {member.function}
                      </span>
                    </div>
                    {/* Assistant linking — only on filming roles. A compact
                        person-icon button opens the list of "assist" crew;
                        checking one mirrors this member's schedule onto them. */}
                    {memberAssistable && (
                      <div className="mt-1 flex items-center gap-1 pl-6 text-[11px] text-muted-foreground">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="flex h-5 shrink-0 items-center rounded px-1 hover:bg-muted hover:text-foreground"
                              title="Assign assistant"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <UserRoundPlus className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="max-h-[220px] overflow-auto"
                          >
                            <DropdownMenuLabel className="text-[11px]">
                              Assign assistant
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {assistCandidates.length === 0 ? (
                              <div className="px-2 py-1 text-[11px] text-muted-foreground">
                                No assist crew on this day
                              </div>
                            ) : (
                              assistCandidates.map((c) => (
                                <DropdownMenuCheckboxItem
                                  key={c.id}
                                  className="text-[11px]"
                                  checked={assistLinks[c.id || ""] === member.id}
                                  onCheckedChange={(checked) =>
                                    setAssistMutation.mutate({
                                      memberId: c.id || "",
                                      assistsId: checked ? member.id || "" : null,
                                    })
                                  }
                                >
                                  {c.name}
                                </DropdownMenuCheckboxItem>
                              ))
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {assignedAssistants.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1">
                            <span>assist:</span>
                            {assignedAssistants.map((a) => (
                              <span
                                key={a.id}
                                className="inline-flex items-center gap-0.5 rounded bg-muted px-1 text-foreground"
                              >
                                {a.name}
                                <button
                                  className="text-muted-foreground hover:text-destructive"
                                  title={`Remove ${a.name} as assistant`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAssistMutation.mutate({
                                      memberId: a.id || "",
                                      assistsId: null,
                                    });
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="truncate">add assistant</span>
                        )}
                      </div>
                    )}
                    {/* Assistant member: read-only status of who they assist. */}
                    {memberIsAssistant && assistingName && (
                      <div className="mt-1 truncate pl-6 text-[11px] text-muted-foreground">
                        assisting: {assistingName}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </ResizablePanelSection>
      </Card>

      {/* Artist Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          setDetailDialogOpen(open);
          if (!open) {
            setSelectedAssignmentForEdit(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedArtist?.artist_name}</DialogTitle>
          </DialogHeader>
          {selectedArtist && (
            <div className="space-y-4">
              <ArtistPhoto artistName={selectedArtist.artist_name} />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Stage</Label>
                  <p>{getStageLabel(selectedArtist)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Set Time</Label>
                  <p>
                    {formatTime(selectedArtist.set_start)} - {formatTime(selectedArtist.set_end)}
                  </p>
                </div>
              </div>

              <FilmedByList
                items={assignments
                  .filter(
                    (a) =>
                      a.assignment_type === "artist" &&
                      a.event_date === selectedArtist.event_date &&
                      assignmentMatchesArtist(a, selectedArtist),
                  )
                  .map((a) => {
                    const member = activeCrewForDate.find((m) => m.id === a.crew_member_id);
                    return {
                      key: a.id ?? `${a.crew_member_id}-${a.filming_start}`,
                      name: member?.name ?? "Crew",
                      fn: member?.function ?? "",
                      filming_start: a.filming_start,
                      filming_end: a.filming_end,
                    };
                  })
                  .sort((x, y) => (x.filming_start ?? "").localeCompare(y.filming_start ?? ""))}
                formatTime={formatTime}
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start filming</Label>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      value={filmingStart}
                      onChange={(e) => setFilmingStart(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setFilmingStart(adjustTime(filmingStart, "subtract"))}
                    >
                      -
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const newStart = adjustTime(filmingStart, "add");
                        setFilmingStart(newStart);
                        // Auto-adjust end time if it would become invalid
                        if (newStart >= filmingEnd) {
                          setFilmingEnd(adjustTime(newStart, "add"));
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>End filming</Label>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      value={filmingEnd}
                      onChange={(e) => setFilmingEnd(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setFilmingEnd(adjustTime(filmingEnd, "subtract"))}
                    >
                      -
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setFilmingEnd(adjustTime(filmingEnd, "add"))}
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={artistNotes}
                  onChange={(e) => setArtistNotes(e.target.value)}
                  placeholder="Only allowed to film him from behind"
                  rows={3}
                />
              </div>

              <div>
                <Label className="mb-2 block">Assign crew</Label>
                <ScrollableList className="space-y-2">
                  {activeCrewForDate.map((member) => {
                    // Only show crew members who have an assignment overlapping the current time range
                    // OR if no assignment is being edited (new assignment mode), show all crew
                    const existingAssignment =
                      selectedAssignmentForEdit?.crew_member_id === member.id
                        ? selectedAssignmentForEdit
                        : assignments.find(
                            (a) =>
                              a.crew_member_id === member.id &&
                              assignmentMatchesArtist(a, selectedArtist) &&
                              a.assignment_type === "artist" &&
                              // Filter by overlapping time range if we have a selected assignment
                              (!selectedAssignmentForEdit ||
                                timeRangesOverlap(
                                  a.filming_start,
                                  a.filming_end,
                                  filmingStart,
                                  filmingEnd,
                                )),
                          );

                    // Always show all crew members so additional crew can be assigned
                    // to the same artist timeslot

                    return (
                      <div key={member.id} className="flex gap-2">
                        <Button
                          variant="outline"
                          className="min-w-0 flex-1 justify-start gap-2"
                          onClick={() => handleSaveArtistAssignment(member.id || "")}
                        >
                          <div
                            className="h-3 w-3 shrink-0 rounded-full"
                            style={{ backgroundColor: member.color || "#666" }}
                          />
                          <span className="truncate">
                            {member.name} - {member.function}
                          </span>
                        </Button>
                        {existingAssignment && isAssistableRole(member.function) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                title="Add an assistant for this slot"
                                className={
                                  (existingAssignment.assist_crew_member_ids ?? []).length > 0
                                    ? "shrink-0 border-primary bg-primary/20 text-primary hover:bg-primary/30"
                                    : "shrink-0"
                                }
                              >
                                <UserRoundPlus className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="max-h-[220px] overflow-auto"
                            >
                              <DropdownMenuLabel className="text-[11px]">
                                Assistants (this slot)
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {assistantCrewForDate.length === 0 ? (
                                <div className="px-2 py-1 text-[11px] text-muted-foreground">
                                  No assist crew on this day
                                </div>
                              ) : (
                                assistantCrewForDate.map((c) => {
                                  const ids = existingAssignment.assist_crew_member_ids ?? [];
                                  return (
                                    <DropdownMenuCheckboxItem
                                      key={c.id}
                                      className="text-[11px]"
                                      checked={ids.includes(c.id || "")}
                                      onCheckedChange={(chk) => {
                                        const next = chk
                                          ? Array.from(new Set([...ids, c.id || ""]))
                                          : ids.filter((x) => x !== c.id);
                                        saveToHistory(
                                          assignments.map((a) =>
                                            assignmentsMatch(a, existingAssignment)
                                              ? { ...a, assist_crew_member_ids: next }
                                              : a,
                                          ),
                                        );
                                        if (
                                          selectedAssignmentForEdit &&
                                          assignmentsMatch(
                                            selectedAssignmentForEdit,
                                            existingAssignment,
                                          )
                                        ) {
                                          setSelectedAssignmentForEdit({
                                            ...selectedAssignmentForEdit,
                                            assist_crew_member_ids: next,
                                          });
                                        }
                                      }}
                                    >
                                      {c.name}
                                    </DropdownMenuCheckboxItem>
                                  );
                                })
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {existingAssignment && canExcludeAssist(member.id || "") && (
                          <Button
                            variant="outline"
                            size="icon"
                            className={
                              existingAssignment.mirror_excluded
                                ? "shrink-0 border-dashed border-muted-foreground text-muted-foreground"
                                : "shrink-0 border-primary bg-primary/20 text-primary hover:bg-primary/30"
                            }
                            title={
                              existingAssignment.mirror_excluded
                                ? "Add assist for this slot"
                                : "Remove assist for this slot"
                            }
                            onClick={() => {
                              const next = !existingAssignment.mirror_excluded;
                              saveToHistory(
                                assignments.map((a) =>
                                  assignmentsMatch(a, existingAssignment)
                                    ? { ...a, mirror_excluded: next }
                                    : a,
                                ),
                              );
                              // Keep the edited-row snapshot in sync so the icon
                              // updates AND "Save changes" doesn't rebuild from a
                              // stale copy that drops mirror_excluded.
                              if (
                                selectedAssignmentForEdit &&
                                assignmentsMatch(selectedAssignmentForEdit, existingAssignment)
                              ) {
                                setSelectedAssignmentForEdit({
                                  ...selectedAssignmentForEdit,
                                  mirror_excluded: next,
                                });
                              }
                            }}
                          >
                            {existingAssignment.mirror_excluded ? (
                              <UserRoundPlus className="h-4 w-4" />
                            ) : (
                              <UserRoundX className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        {existingAssignment && (
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => {
                              // Remove only this specific assignment using exact matching (including times)
                              const filtered = assignments.filter(
                                (a) => !assignmentsMatch(a, existingAssignment),
                              );
                              saveToHistory(filtered);

                              // Close dialog if we're editing this assignment
                              if (
                                selectedAssignmentForEdit &&
                                assignmentsMatch(selectedAssignmentForEdit, existingAssignment)
                              ) {
                                setDetailDialogOpen(false);
                                setSelectedAssignmentForEdit(null);
                              }
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </ScrollableList>
              </div>

              <div className="flex justify-end border-t pt-4">
                <Button
                  onClick={() => {
                    // Update the assignment times if editing an existing one
                    if (selectedAssignmentForEdit) {
                      const start = normalizeTime(filmingStart);
                      const end = normalizeTime(filmingEnd);

                      // Guard against double-booking this crew member when the
                      // edited time now collides with another of their blocks.
                      // The block being edited is excluded from the check.
                      if (
                        checkOverlap(
                          selectedAssignmentForEdit.crew_member_id,
                          start,
                          end,
                          selectedAssignmentForEdit,
                        )
                      ) {
                        toast.error(CREW_CONFLICT_MESSAGE);
                        return;
                      }

                      const filtered = assignments.filter(
                        (a) => !assignmentsMatch(a, selectedAssignmentForEdit),
                      );
                      const updated = {
                        ...selectedAssignmentForEdit,
                        filming_start: start,
                        filming_end: end,
                        notes: artistNotes,
                      };
                      saveToHistory([...filtered, updated]);
                    }
                    setDetailDialogOpen(false);
                    setSelectedAssignmentForEdit(null);
                  }}
                >
                  Save changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Subject Dialog */}
      <Dialog
        open={customDialogOpen}
        onOpenChange={(open) => {
          setCustomDialogOpen(open);
          if (!open) {
            setSelectedAssignmentForEdit(null);
            setCustomSubject("");
            setCustomLocation("");
            setCustomStageId(null);
            setCustomNotes("");
            setCustomStart("");
            setCustomEnd("");
            setSelectedCrewForCustom("");
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add Custom Subject</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crew member</Label>
              <Select value={selectedCrewForCustom} onValueChange={setSelectedCrewForCustom}>
                <SelectTrigger>
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {activeCrewForDate
                    .filter((m) => m.id)
                    .map((member) => (
                      <SelectItem key={member.id} value={member.id!}>
                        {member.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Subject *</Label>
              <Input
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveCustomDialog();
                  }
                }}
                placeholder="e.g., Backstage interviews"
              />
            </div>

            <div>
              <Label>Stage</Label>
              <Select
                value={customStageId ?? "__none__"}
                onValueChange={(v) => setCustomStageId(v === "__none__" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stagesForSelectedDate.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="__none__">Other / no stage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Location notes</Label>
              <Input
                value={customLocation}
                onChange={(e) => setCustomLocation(e.target.value)}
                placeholder="Optional, e.g., Backstage entrance"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="Additional details..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start time *</Label>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomStart(adjustTime(customStart, "subtract"))}
                  >
                    -
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newStart = adjustTime(customStart, "add");
                      setCustomStart(newStart);
                      // Auto-adjust end time if it would become invalid
                      if (newStart >= customEnd) {
                        setCustomEnd(adjustTime(newStart, "add"));
                      }
                    }}
                  >
                    +
                  </Button>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">End time *</Label>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomEnd(adjustTime(customEnd || customStart, "subtract"))}
                  >
                    -
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomEnd(adjustTime(customEnd || customStart, "add"))}
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {selectedAssignmentForEdit?.assignment_type === "custom" && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    const filtered = assignments.filter(
                      (a) => !assignmentsMatch(a, selectedAssignmentForEdit),
                    );
                    saveToHistory(filtered);
                    setCustomDialogOpen(false);
                    setSelectedAssignmentForEdit(null);
                  }}
                  className="flex-1"
                >
                  Delete
                </Button>
              )}
              <Button onClick={saveCustomDialog} className="flex-1">
                {selectedAssignmentForEdit ? "Save Changes" : "Add Subject"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generation Dialog */}
      {/* Client feedback mirrored into the creator, so you can see and resolve
          it right where you edit the schedule. */}
      {briefingId && <ScheduleFeedbackPanel productionId={briefingId} formatTime={formatTime} />}

      {/* Share with client (mode reflects the production's real status) */}
      <ShareScheduleDialog open={shareOpen} onOpenChange={setShareOpen} productionId={briefingId} />

      <AiGenerationDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        crew={crewForDate}
        artists={artistsForDate}
        stages={stages}
        selectedDate={selectedDate}
        briefingId={briefingId}
        existingAssignments={assignments.filter((a) => a.event_date === selectedDate)}
        onAssignmentsGenerated={handleAiAssignmentsGenerated}
        onSettingsSaved={handleAiSettingsSaved}
        previousAiSettings={previousAiSettings ?? undefined}
        autoGenerate={autoGenerate}
      />

      {/* AI Regenerate Dialog */}
      <AiRegenerateDialog
        open={aiRegenerateDialogOpen}
        onOpenChange={setAiRegenerateDialogOpen}
        onRegenerateWithPrevious={handleRegenerateWithPrevious}
        onRegenerateWithNew={handleRegenerateWithNew}
        onClear={handleClearGenerated}
        onRegenerateWithInstructions={handleRegenerateWithInstructions}
      />
    </div>
  );
}
